import type en from "./en";

/**
 * Hindi (हिन्दी).
 *
 * Typed as `typeof en`, so this file will not compile until every key in the
 * English source has a counterpart here. That is deliberate: a missing key
 * should stop the build, not ship an English sentence to a Hindi reader.
 *
 * Translation notes for whoever edits this next:
 *
 *  - Product and company names stay in Latin script — Kaympus, Kayease, CBSE,
 *    ICSE, ERP, UPI, Razorpay. Schools in this market read them that way, and
 *    transliterating "Kaympus" to कैम्पस collides with the ordinary Hindi word
 *    for campus, which is exactly the confusion a brand name must avoid.
 *  - Register is the polite-plural आप / करें used in Indian school
 *    correspondence, not the informal तू/तुम.
 *  - Hindi runs roughly 10-20% longer than English and puts the verb last, so
 *    headings are re-ordered rather than translated word by word. The <em>
 *    accent marker moves with the phrase it belongs to — see the note at the
 *    top of en.ts.
 *  - Figures, currency and dates stay in Western digits, matching how Indian
 *    school software and receipts actually print them.
 */
const hi: typeof en = {
  common: {
    skipToContent: "मुख्य सामग्री पर जाएँ",
  },

  language: {
    change: "भाषा बदलें",
  },

  nav: {
    features: "विशेषताएँ",
    pricing: "कीमत",
    contact: "संपर्क",
    blog: "ब्लॉग",
    support: "सहायता",
    login: "लॉगिन",
    freeTrial: "फ्री ट्रायल",
    startFreeTrial: "फ्री ट्रायल शुरू करें",
    toggleMenu: "मेन्यू खोलें या बंद करें",
    home: "Kaympus होम",
  },

  hero: {
    titleLine1: "स्मार्ट स्कूल।",
    titleLine2: "मज़बूत रिश्ते।",
    titleAccent: "बेहतर भविष्य।",
    subtitle:
      "एक संपूर्ण स्कूल मैनेजमेंट सिस्टम और ERP सॉफ़्टवेयर — पढ़ाई, प्रशासन और अभिभावकों से संवाद, सब कुछ एक ही जगह से।",
    ctaDemo: "फ्री डेमो बुक करें",
    ctaWatch: "ओवरव्यू देखें",
    dashboardAlt:
      "Kaympus एडमिन डैशबोर्ड — छात्र, शिक्षक, स्टाफ़ और फीस कलेक्शन का कुल विवरण",
    phoneAlt:
      "Kaympus स्टूडेंट मोबाइल ऐप का होम स्क्रीन — आज की उपस्थिति और कक्षाएँ",
    modalTitle: "Kaympus स्कूल ERP: प्लेटफ़ॉर्म ओवरव्यू",
    modalClose: "वीडियो बंद करें",
    modalHeading: "ओवरव्यू वीडियो जल्द आ रहा है",
    modalBody:
      "हम पूरे प्लेटफ़ॉर्म का वॉकथ्रू तैयार कर रहे हैं। तब तक, एक निजी डेमो में Kaympus को लाइव देखिए।",
    modalCta: "लाइव डेमो बुक करें",
  },

  problem: {
    heading: "स्कूल चलाना\n<em>आग बुझाने जैसा</em> नहीं होना चाहिए।",
    items: [
      "फीस की रसीद के लिए अभिभावकों के लगातार फ़ोन।",
      "शिक्षकों का हाथ से उपस्थिति रजिस्टर भरना।",
      "बिखरी हुई Excel फ़ाइलों में उलझा प्रशासन।",
      "परीक्षा के समय रिपोर्ट बनाने की अफ़रा-तफ़री।",
      "कागज़ी रजिस्टरों में खोई हुई एडमिशन पूछताछ।",
    ],
    cardTitleLine1: "Kaympus का",
    cardTitleLine2: "सफलता मानक",
    cardBody:
      "ज़्यादातर स्कूल अलग-अलग जोड़-तोड़ वाले सॉफ़्टवेयर चलाते हैं। Kaympus इस अव्यवस्था की जगह पूरी डिजिटल स्पष्टता देता है।",
    shiftLabel: "असल में क्या बदलता है",
    shiftBody:
      "ज़्यादातर स्कूलों में फीस Excel पर चलती है, उपस्थिति रजिस्टर में दर्ज होती है, और हर रिमाइंडर WhatsApp ग्रुप में हाथ से टाइप करके भेजा जाता है। जब एडमिशन, उपस्थिति और फीस एक ही रिकॉर्ड पर आ जाते हैं, तो रसीदें जारी होते ही नंबर के साथ बनती हैं और फीस रिमाइंडर ड्यू डेट के बाद नहीं, पहले चले जाते हैं।",
    proofFeatures: "लाइव फ़ीचर",
    proofModules: "मॉड्यूल",
    proofDashboards: "रोल डैशबोर्ड",
  },

  whyFail: {
    heading: "ज़्यादातर स्कूल ERP <em>क्यों नाकाम होते हैं</em>",
    bodyLead:
      "ज़्यादातर स्कूल ERP सॉफ़्टवेयर फ़ीचर की कमी से नहीं, अमल की कमी से नाकाम होते हैं।",
    bodyStrong: "कई स्कूल 6 महीने के भीतर वापस Excel पर लौट आते हैं।",
    bodyTail: "यही वह कड़वी सच्चाई है जिसे हम हल कर रहे हैं।",
    failTitle: "आमतौर पर क्या होता है",
    failSubtitle: "नाकामी का चक्र",
    winTitle: "Kaympus अलग क्यों है",
    winSubtitle: "Kaympus का मानक",
    schoolAlt: "स्कूल चित्रण",
    fails: [
      "शिक्षकों के लिए बहुत जटिल",
      "ठीक से ऑनबोर्डिंग सहायता नहीं",
      "साइन-अप के बाद छिपे हुए शुल्क",
      "परीक्षा के समय धीमी रफ़्तार",
      "भुगतान के बाद कमज़ोर सपोर्ट",
      "भारतीय स्कूलों की हक़ीक़त के हिसाब से नहीं बना",
      "फीस पेमेंट इंटीग्रेशन का बार-बार बंद होना",
    ],
    benefits: [
      "ग़ैर-तकनीकी उपयोगकर्ताओं के लिए सरल इंटरफ़ेस",
      "व्यवस्थित ऑनबोर्डिंग और निर्देशित सेटअप",
      "पारदर्शी कीमत, कोई छिपा हुआ शुल्क नहीं",
      "परीक्षा और फीस के व्यस्त दिनों के लिए तैयार",
      "बढ़ते स्कूलों के लिए समर्पित सपोर्ट",
      "भारतीय शैक्षणिक ढाँचे के अनुरूप बनाया गया",
      "भरोसेमंद मल्टी-गेटवे पेमेंट इंटीग्रेशन",
    ],
  },

  showcase: {
    heading: "देखिए Kaympus की <em>ख़ास विशेषताएँ</em>",
    body:
      "वे सुविधाएँ जो ज़्यादातर स्कूल ERP में नहीं मिलतीं: बायोमेट्रिक पेरोल, एडमिशन, वेबसाइट CMS, डिजिटल सहमति और मल्टी-स्कूल नियंत्रण — सब एक ही सिस्टम में।",
    exploreAll: "सभी विशेषताएँ देखें",
    /* तीन फ़ील्ड, और असली वज़न `imageAlt` का है। पैनल में अब सिर्फ़ पाँच असली
       प्रोडक्ट स्क्रीनशॉट हैं — विंडो फ़्रेम, आँकड़ों के दो कार्ड और नीचे की
       पट्टी हटा दी गई है — इसलिए हर स्क्रीन का एकमात्र विवरण यही alt टेक्स्ट
       है, स्क्रीन रीडर के लिए भी और इमेज क्रॉलर के लिए भी। "डैशबोर्ड
       स्क्रीनशॉट" नहीं, स्क्रीन पर जो सचमुच है वही लिखें। देखें:
       ProductShowcase.tsx का FEATURE_SHOTS — कोई कैप्चर दोबारा लिया जाए तो
       दोनों भाषाओं में alt भी बदलें। */
    features: [
      {
        title: "उपस्थिति से पेरोल तक",
        subtitle: "बायोमेट्रिक पंच सीधे पेरोल तक पहुँचते हैं",
        imageAlt:
          "Kaympus रन पेरोल स्क्रीन: 17 स्टाफ़ का ग्रॉस, कटौती और नेट वेतन उनकी अपनी उपस्थिति से गणना होकर, और फ़ाइनल करने से पहले तीन बिना स्वीकृति वाली अनुपस्थितियाँ चिह्नित।",
      },
      {
        title: "पूछताछ से एडमिशन तक",
        subtitle: "पहली पूछताछ से लेकर छात्र की पूरी प्रोफ़ाइल तक",
        imageAlt:
          "Kaympus एडमिशन आवेदन स्क्रीन: स्कूल की अपनी वेबसाइट से आए आवेदन, हर एक के साथ अभिभावक का संपर्क और स्थिति — नया, समीक्षित, संपर्क किया गया या स्वीकृत।",
      },
      {
        title: "वेबसाइट और कस्टम डोमेन",
        subtitle: "अपनी वेबसाइट और डोमेन Kaympus से ही चलाइए",
        imageAlt:
          "Kaympus वेबसाइट कंटेंट स्क्रीन: स्कूल की सार्वजनिक वेबसाइट ERP के भीतर से ही संपादित — नाम, टैगलाइन, लोगो, मेटा टाइटल और विवरण, कुल अठारह पेज सेक्शन में।",
      },
      {
        title: "डिजिटल सहमति फ़ॉर्म",
        subtitle: "अभिभावकों की सहमति बनाइए, भेजिए और ट्रैक कीजिए",
        imageAlt:
          "Kaympus सहमति फ़ॉर्म स्क्रीन: एक ही कक्षा के लिए पाँच फ़ॉर्म — वार्षिकोत्सव, मीडिया अनुमति, स्वास्थ्य जाँच, खेल दिवस और शैक्षणिक भ्रमण — हर एक पर कितने अभिभावकों ने भरा और कितने बाक़ी हैं।",
      },
      {
        title: "मल्टी-स्कूल और रोल नियंत्रण",
        subtitle: "हर भूमिका और हर कैंपस के लिए अलग डैशबोर्ड",
        imageAlt:
          "Kaympus अनुमतियाँ स्क्रीन: हर व्यक्ति और हर सेक्शन के लिए रीड, राइट, अपडेट और डिलीट अधिकार, जिन पर विभाग और पदनाम के अधिकार हावी नहीं होते बल्कि जुड़ते हैं।",
      },
    ],
  },

  roi: {
    heading: "जानिए <em>Kaympus आपके स्कूल की कितनी बचत करता है</em>",
    body:
      "स्लाइडर को अपने स्कूल के आकार के हिसाब से सरकाइए और आँकड़े बदलते देखिए — फीस वसूली, स्टाफ़ के घंटे और कागज़ का ख़र्च, पूरे शैक्षणिक वर्ष का हिसाब।",
    panelTitle: "आपका स्कूल, मोटे तौर पर",
    reset: "रीसेट",
    closestMatch: "सबसे नज़दीकी",
    custom: "कस्टम",
    presetSmall: "छोटा",
    presetGrowing: "बढ़ता हुआ",
    presetLarge: "बड़ा",
    presetGroup: "ग्रुप",
    sliderStudents: "कुल नामांकित छात्र",
    sliderMonthlyFee: "औसत मासिक फीस / छात्र",
    sliderStaff: "शिक्षक और प्रशासनिक स्टाफ़",
    unitStudents: "छात्र",
    unitStaff: "स्टाफ़",
    unitPerMonth: "/माह",
    ariaPerMonth: "{value} प्रति माह",
    crore: "करोड़",
    lakh: "लाख",
    disclaimer:
      "यह अनुमान 500+ भारतीय पार्टनर स्कूलों के औसत पर आधारित है। आपकी ऑनबोर्डिंग टीम आपके स्कूल के असली आँकड़े निकालकर देगी।",
    gainsLabel: "एक साल में आपके स्कूल को क्या मिलता है",
    moneyBack: "पैसे की बचत",
    moneyBackNote: "लगभग {value} हर महीने",
    staffTime: "स्टाफ़ के बचे घंटे",
    staffTimeNote: "लगभग {value} घंटे / स्टाफ़",
    hoursSuffix: "घंटे",
    whereFrom: "यह बचत कहाँ से आती है",
    rowFeesLabel: "समय पर वसूली गई फीस",
    rowFeesNote:
      "अंदर ही मौजूद फीस ट्रैकिंग और स्वतः भेजे जाने वाले ऑनलाइन पेमेंट लिंक बकाया फीस पूरी कराते हैं।",
    rowPaperLabel: "कागज़ और प्रिंटिंग की बचत",
    rowPaperNote: "रिपोर्ट कार्ड, सर्कुलर और रसीदें सब डिजिटल।",
    perStudentBefore: "यानी आपके हर नामांकित छात्र पर लगभग ",
    perStudentAfter: " की बचत।",
    ctaNote: "अपने स्कूल के असली आँकड़े जानना चाहते हैं?",
    ctaButton: "मेरे स्कूल के सटीक आँकड़े भेजें",
  },

  multiSchool: {
    heading: "<em>सिंगल और मल्टी-ब्रांच</em>\nस्कूलों के लिए\nबनाया गया",
    body:
      "Kaympus सुरक्षित मल्टी-टेनेंट आर्किटेक्चर पर चलता है — यानी हर स्कूल को अपना <em>अलग डेटाबेस</em> और पूरी तरह स्वतंत्र डिजिटल परिवेश मिलता है।",
    diagramAria: "मल्टी-टेनेंट आर्किटेक्चर के लाभ",
    benefits: [
      { title: "पूर्ण डेटा सुरक्षा", desc: "दो संस्थानों का डेटा कभी नहीं मिलता।" },
      { title: "अलग लॉगिन पोर्टल", desc: "हर स्कूल का अपना ब्रांडेड प्रवेश द्वार।" },
      { title: "समर्पित डेटाबेस", desc: "सबसे सुरक्षित क्लाउड आर्किटेक्चर।" },
      { title: "फ़्रैंचाइज़ी के लिए तैयार", desc: "एजुकेशन ग्रुप और नेटवर्क के लिए उपयुक्त।" },
      { title: "स्वतंत्र पेमेंट गेटवे", desc: "हर स्कूल की अलग पेमेंट सेटिंग।" },
      { title: "कस्टम सबडोमेन", desc: "हर ब्रांच के लिए अपना Kaympus सबडोमेन।" },
    ],
  },

  audience: {
    heading: "<em>Kaympus</em>\nकिसके लिए है?",
    body:
      "आप 200 छात्र सँभालते हों या 2,000 — सिस्टम आपके आकार के अनुसार ढल जाता है। हमारा ख़ास ध्यान टियर 2 और 3 शहरों के बढ़ते स्कूलों पर है।",
    checklist: [
      "बजट के अनुकूल",
      "कोई सेटअप झंझट नहीं",
      "तय कीमत, कोई बढ़ोतरी नहीं",
      "मोबाइल पर आसान",
    ],
    cards: [
      {
        title: "प्राइवेट स्कूल",
        desc: "पारंपरिक स्कूलों को एकीकृत डिजिटल व्यवस्था में बदलिए।",
      },
      {
        title: "CBSE / ICSE",
        desc: "पाठ्यक्रम के अनुरूप उपकरण, बेहतर शैक्षणिक परिणामों के लिए।",
      },
      {
        title: "K-12 और प्ले स्कूल",
        desc: "एडमिशन से लेकर सेक्शन ट्रांसफ़र और डिजिटल TC तक, पूरा काम एक जगह।",
      },
      {
        title: "टियर 2 और 3 शहर",
        desc: "उभरते बाज़ारों के लिए बनी किफ़ायती तकनीक।",
      },
    ],
  },

  howItWorks: {
    heading: "<em>3 आसान चरणों</em> में शुरू कीजिए",
    body:
      "किसी IT टीम की ज़रूरत नहीं। हमारा निर्देशित सेटअप आपके पूरे स्कूल को 3 आसान चरणों में लाइव कर देता है।",
    stepLabel: "चरण",
    steps: [
      {
        title: "साइन अप और सेटअप",
        desc: "अपना स्कूल बनाइए, शैक्षणिक ढाँचा तय कीजिए, कक्षाएँ जोड़िए और स्टाफ़ को आमंत्रित कीजिए।",
        items: [
          "स्कूल प्रोफ़ाइल सेटअप",
          "बुनियादी ढाँचा और विवरण",
          "एडमिन और समन्वयक जोड़ें",
          "शैक्षणिक वर्ष तय करें",
        ],
      },
      {
        title: "अपनी टीम जोड़िए",
        desc: "शिक्षक, प्रशासक और छात्रों को जोड़िए। हर किसी को अपना निजी पोर्टल मिलता है।",
        items: [
          "भूमिका आधारित खाते",
          "डेटा आयात और आवंटन",
          "स्टाफ़ की ज़िम्मेदारियाँ",
          "अनुमति प्रबंधन",
        ],
      },
      {
        title: "चलाइए और स्वचालित कीजिए",
        desc: "पूरा स्कूल एक डैशबोर्ड से चलाइए। उपस्थिति, फीस और रिपोर्ट सब स्वतः।",
        items: [
          "लाइव उपस्थिति ट्रैकिंग",
          "स्वचालित फीस वसूली",
          "तुरंत परिणाम प्रकाशन",
          "स्मार्ट एनालिटिक्स",
        ],
      },
    ],
  },

  faq: {
    heading: "सवाल हैं?\nहमारे पास <em>जवाब हैं।</em>",
    stillNeedHelp: "अब भी मदद चाहिए?",
    contactSupport: "हमारी सपोर्ट टीम से संपर्क करें",
    categories: {
      general: {
        label: "सामान्य",
        items: [
          {
            question: "Kaympus क्या है?",
            answer:
              "Kaympus एक संपूर्ण स्कूल मैनेजमेंट प्लेटफ़ॉर्म है। एडमिशन, छात्र और स्टाफ़ रिकॉर्ड, उपस्थिति, समय-सारणी, फीस, परीक्षा और रिपोर्ट कार्ड, पुस्तकालय, छात्रावास, परिवहन, HR और पेरोल तथा पूरे स्कूल का संवाद — सब एक ही लॉगिन से चलता है।",
          },
          {
            question: "Kaympus का उपयोग कौन कर सकता है?",
            answer:
              "Kaympus K-12 स्कूलों, CBSE/ICSE संस्थानों, जूनियर कॉलेजों और मल्टी-ब्रांच स्कूल ग्रुप के लिए बना है। इसमें स्कूल एडमिन, शिक्षकों, छात्रों और ग़ैर-शैक्षणिक स्टाफ़ के लिए अलग-अलग लॉगिन मिलते हैं, और स्टाफ़ की भूमिकाएँ आपके अपने पदनामों से बनती हैं। अभिभावक अपने बच्चे के लॉगिन और मोबाइल ऐप से जुड़े रहते हैं।",
          },
          {
            question: "क्या मेरे स्कूल का डेटा सुरक्षित है?",
            answer:
              "हाँ। हर स्कूल अपने अलग, स्वतंत्र वर्कस्पेस और अपने डोमेन पर चलता है, और सारा ट्रैफ़िक एन्क्रिप्टेड HTTPS पर जाता है। पहुँच विस्तृत रोल और अनुमति सेटिंग से नियंत्रित होती है, लॉगिन OTP से सत्यापित होता है, और संवेदनशील गतिविधियाँ एक ऐक्टिविटी लॉग में दर्ज रहती हैं जिसे आप कभी भी जाँच सकते हैं।",
          },
          {
            question: "क्या Kaympus दूसरे टूल से जुड़ सकता है?",
            answer:
              "हाँ। अपना पेमेंट गेटवे जोड़िए (Razorpay, PayU, PhonePe, Paytm, Cashfree, Stripe या PayPal), साथ ही OTP और अलर्ट के लिए DLT-पंजीकृत SMS गेटवे और अपना SMTP ईमेल खाता। स्टाफ़ उपस्थिति में बायोमेट्रिक और फ़ेस एनरोलमेंट डिवाइस भी चलते हैं।",
          },
          {
            question: "लागू करने में कितना समय लगता है?",
            answer:
              "ज़्यादातर स्कूल कुछ ही दिनों में लाइव हो जाते हैं। हमारी टीम आपकी कक्षाएँ, फीस हेड और ग्रेडिंग व्यवस्था सेट करती है, मौजूदा छात्र और स्टाफ़ डेटा आयात करती है, और लाइव होने से पहले आपकी टीम को प्रशिक्षण देती है। सही समय इस बात पर निर्भर करता है कि आप कितना पुराना डेटा साथ ला रहे हैं।",
          },
        ],
      },
      features: {
        label: "विशेषताएँ",
        items: [
          {
            question: "ऑनलाइन फीस वसूली कैसे काम करती है?",
            answer:
              "आप हर कक्षा के लिए फीस हेड और संरचना तय करते हैं, और अभिभावक पोर्टल या मोबाइल ऐप से आपके जुड़े गेटवे के ज़रिए UPI, कार्ड या नेट बैंकिंग से भुगतान करते हैं। हर भुगतान पर क्रमांकित डिजिटल रसीद मिलती है जिसे आप प्रिंट या साझा कर सकते हैं, और फीस रिमाइंडर ऐप नोटिफ़िकेशन तथा पोर्टल अलर्ट से अपने आप चले जाते हैं।",
          },
          {
            question: "क्या हम बोर्ड के अनुरूप रिपोर्ट कार्ड बना सकते हैं?",
            answer:
              "हाँ। रिपोर्ट कार्ड इंजन आपकी अपनी ग्रेडिंग व्यवस्था पर चलता है और CBSE जैसे सख़्त पास नियम भी सँभालता है, जहाँ थ्योरी में फेल होने पर पूरा विषय फेल माना जाता है। कार्ड पर आपके स्कूल का बोर्ड और संबद्धता संख्या के साथ लोगो, प्रधानाचार्य के हस्ताक्षर और मुहर छपती है।",
          },
          {
            question: "उपस्थिति मॉड्यूल में क्या-क्या आता है?",
            answer:
              "शिक्षक रोज़ाना कक्षा की उपस्थिति दर्ज करते हैं, जबकि स्टाफ़ उपस्थिति में बायोमेट्रिक और फ़ेस एनरोलमेंट, जियोफ़ेंस चेक-इन और सुधार के लिए पंच समीक्षा स्क्रीन मिलती है। कक्षा, छात्र और स्टाफ़ की उपस्थिति रिपोर्ट जब चाहें निर्यात कर सकते हैं।",
          },
        ],
      },
      security: {
        label: "सुरक्षा",
        items: [
          {
            question: "हमारे स्कूल का डेटा कहाँ रखा जाता है?",
            answer:
              "Kaympus पूरी तरह क्लाउड पर है और एन्क्रिप्टेड HTTPS से चलता है, और हर स्कूल अपने अलग वर्कस्पेस में रहता है ताकि दो स्कूलों का डेटा कभी न मिले। बैकअप नियमित रूप से लिए जाते हैं।",
          },
          {
            question: "डेटा का मालिक कौन है और क्या हम उसे निर्यात कर सकते हैं?",
            answer:
              "आपके स्कूल के डेटा पर 100% मालिकाना हक़ आपका ही रहता है। छात्र रिकॉर्ड, फीस खाता-बही, उपस्थिति रजिस्टर और रिपोर्ट कार्ड कभी भी Excel या PDF में निर्यात किए जा सकते हैं।",
          },
        ],
      },
      implementation: {
        label: "क्रियान्वयन",
        items: [
          {
            question: "क्या Excel या पुराने ERP से डेटा लाना मुफ़्त है?",
            answer:
              "हाँ, सहायता सहित माइग्रेशन शामिल है। हमारी टीम आपके मौजूदा Excel या CSV रिकॉर्ड बिना किसी अतिरिक्त शुल्क के आयात करती है, साफ़ करती है और व्यवस्थित करती है।",
          },
          {
            question: "क्या आप स्टाफ़ और शिक्षकों का प्रशिक्षण देते हैं?",
            answer:
              "हाँ। हम शिक्षकों और एडमिन के लिए इंटरैक्टिव ऑनलाइन प्रशिक्षण सत्र चलाते हैं, साथ में लिखित यूज़र गाइड और वीडियो वॉकथ्रू देते हैं जिन्हें आपकी टीम कभी भी दोबारा देख सकती है।",
          },
        ],
      },
      support: {
        label: "सपोर्ट",
        items: [
          {
            question: "आप किन माध्यमों से सहायता देते हैं?",
            answer:
              "ज़रूरी सवालों के लिए सीधे फ़ोन और ईमेल सपोर्ट मिलता है, साथ ही Kaympus के भीतर ही एक हेल्पडेस्क है जहाँ आपका स्टाफ़ टिकट बनाकर उसका समाधान होने तक ट्रैक कर सकता है।",
          },
          {
            question: "क्या अभिभावकों और छात्रों को ऐप इंस्टॉल करना ज़रूरी है?",
            answer:
              "Kaympus मोबाइल ऐप छात्रों और अभिभावकों को फीस, उपस्थिति, परिणाम, गृहकार्य और स्कूल की सूचनाएँ उनके फ़ोन पर देता है। यह सब ब्राउज़र पर भी उपलब्ध है, इसलिए कुछ भी सिर्फ़ ऐप तक सीमित नहीं है।",
          },
        ],
      },
    },
  },

  features: {
    hero: {
      /* Re-ordered, not translated word for word: Hindi puts the verb last, so
         the accent run carries "पूरा स्कूल चलाइए" and the lead carries the
         adverbial "एक ही स्क्रीन से". Still four words — FeaturesHero staggers
         a fixed count. */
      headlineLead: ["एक", "ही", "स्क्रीन", "से"],
      headlineAccent: "पूरा स्कूल चलाइए।",
      subtitle:
        "हमारा पूरा ERP सुइट देखिए — एडमिशन और उपस्थिति से लेकर फीस वसूली, पेरोल, पुस्तकालय, परिवहन और अभिभावकों से लाइव संवाद तक।",
      ctaDemo: "फ्री डेमो बुक करें",
      ctaWatch: "ओवरव्यू देखें",
      cardTitle: "Kaympus ERP: फ़ीचर सुइट",
      cardTiles: [
        "छात्र और स्टाफ़",
        "उपस्थिति",
        "फीस वसूली",
        "परीक्षाएँ",
        "संवाद",
        "एनालिटिक्स",
        "खेल प्रबंधन",
        "पुस्तकालय",
        "ट्रांसपोर्ट GPS",
        "HR और पेरोल",
        "छात्रावास प्रबंधन",
        "अवकाश प्रबंधन",
      ],
      cardCount: "{count} विशेषताएँ",
      cardAllIncluded: "सब शामिल",
      modalTitle: "Kaympus स्कूल ERP: फ़ीचर ओवरव्यू",
      modalVideoTitle: "Kaympus ERP ओवरव्यू वीडियो",
      modalClose: "वीडियो बंद करें",
    },

    catalogue: {
      heading: "आपके स्कूल को जो चाहिए, सब कुछ।\n<em>जो नहीं चाहिए, कुछ नहीं।</em>",
      intro:
        "{categories} श्रेणियों में फैली {features} विशेषताएँ, एक ही जुड़े हुए सिस्टम में — अलग-अलग ऐड-ऑन का गट्ठर नहीं। नीचे उन चार लोगों के लिए मुख्य बातें हैं जो इसे रोज़ इस्तेमाल करते हैं: उन्हें क्या मिलता है, आपके स्कूल में क्या बदलता है, और यह बाक़ी स्कूल ERP के बनने के तरीक़े से कैसे अलग है। पूरा वॉकथ्रू डेमो में है।",
      workspaceTitle: "{role} वर्कस्पेस",
      whatYouGet: "आपको क्या मिलता है",
      howItHelps: "आपके स्कूल को इससे क्या मिलता है",
      differentHeading: "यह बाक़ी स्कूल ERP से कैसे अलग है",
      usualApproach: "आम तरीक़ा",
      withKaympus: "Kaympus के साथ",
      ctaTitle: "अपने ही डेटा पर चलते हुए सभी {count} देखिए।",
      ctaBody: "30 मिनट का वॉकथ्रू, आपकी असली कक्षाओं, फीस और स्टाफ़ के साथ। कोई स्लाइड नहीं।",
      ctaButton: "लाइव डेमो बुक करें",
    },

    roles: {
      admin: { label: "एडमिन", blurb: "प्रधानाचार्य और दफ़्तर के लिए" },
      teacher: { label: "शिक्षक", blurb: "कक्षा के लिए, पिछले दफ़्तर के लिए नहीं" },
      staff: { label: "स्टाफ़", blurb: "लेखा, पुस्तकालय, छात्रावास और फ्रंट डेस्क के लिए" },
      student: { label: "छात्र और अभिभावक", blurb: "परिवार के लिए, उनके फ़ोन पर" },
    },

    groups: {
      "admin-overview": { title: "एक नज़र में स्कूल", tagline: "लाइव आँकड़े, गड़बड़ियाँ सबसे पहले" },
      "admin-people": { title: "छात्र और स्टाफ़ रिकॉर्ड", tagline: "हर व्यक्ति का एक ही रिकॉर्ड" },
      "admin-academics": { title: "शिक्षण और परीक्षाएँ", tagline: "समय-सारणी से रिपोर्ट कार्ड तक" },
      "admin-finance": { title: "फीस, पेरोल और अनुपालन", tagline: "आय और व्यय, दोनों का हिसाब" },
      "admin-operations": { title: "कैंपस और संवाद", tagline: "सुविधाएँ, सूचनाएँ, वेबसाइट" },
      "teacher-day": { title: "मेरा पढ़ाने का दिन", tagline: "आज के पीरियड और उपस्थिति" },
      "teacher-classroom": { title: "कक्षा कार्य और मूल्यांकन", tagline: "गृहकार्य, टेस्ट और परिणाम" },
      "teacher-app": { title: "शिक्षक मोबाइल ऐप", tagline: "कक्षा, आपकी जेब में" },
      "teacher-self": { title: "अवकाश, वेतन और रिपोर्ट", tagline: "अपनी छुट्टी और अपनी पेस्लिप" },
      "staff-workspace": { title: "आपका अपना लॉगिन", tagline: "आपके काम तक सीमित" },
      "staff-app": { title: "स्टाफ़ मोबाइल ऐप", tagline: "पंच कीजिए, आवेदन दीजिए, डाउनलोड कीजिए" },
      "staff-frontdesk": { title: "फ्रंट ऑफ़िस और फीस काउंटर", tagline: "काउंटर और उसकी क़तार" },
      "staff-facilities": { title: "पुस्तकालय, छात्रावास और सुविधाएँ", tagline: "किताबें, कमरे और बसें" },
      "student-home": { title: "परिवार का नज़रिया", tagline: "एक बच्चा, एक साफ़ तस्वीर" },
      "student-app": { title: "अभिभावक और छात्र ऐप", tagline: "जिस दिन हो, उसी दिन सूचना" },
      "student-academics": { title: "कक्षा कार्य, परीक्षा और परिणाम", tagline: "गृहकार्य, परीक्षाएँ और परिणाम" },
      "student-fees": { title: "फीस और भुगतान", tagline: "बकाया, जमा, और ऑनलाइन देय" },
    },

    narratives: {
      "admin-overview": {
        what: "वह स्क्रीन जिस पर प्रधानाचार्य या प्रशासक सबसे पहले पहुँचते हैं। एक ही पेज, जो उन सवालों का जवाब देता है जिनके लिए आपको वरना पूरी बिल्डिंग घूमनी पड़ती: आज कितने बच्चे आए हैं, फीस काउंटर पर कितना आया, कौन नहीं आया, और किस बात पर आपका जवाब अटका है।",
        provides: [
          "एक डैशबोर्ड जो स्कूल के हर हिस्से से लाइव पढ़ता है, कल रात की प्रक्रिया से नहीं",
          "आज की उपस्थिति, आज की वसूली और आज की स्टाफ़ हाज़िरी, साथ-साथ",
          "गड़बड़ियाँ सबसे पहले: अनुपस्थित छात्र, बकाया फीस, बिना जवाब की शिकायतें",
        ],
        helps: [
          "कल की वसूली और आज की स्टाफ़ हाज़िरी आपको चाय के पहले कप से पहले पता होती है",
          "सुबह की शुरुआत उन गिनी-चुनी चीज़ों से होती है जो ग़लत हैं, उन्हें ढूँढ़ने से नहीं",
          "प्रबंधन बैठक में उठा सवाल उसी बैठक में हल हो जाता है",
        ],
        different: {
          others: "ज़्यादातर स्कूल ERP स्थिर टाइलों की दीवार पर खुलते हैं जो रात की प्रक्रिया से भरती हैं, इसलिए स्क्रीन पर दिख रहा आँकड़ा कल का होता है, और उसके पीछे के छात्रों तक पहुँचने के लिए Excel में निर्यात करना पड़ता है।",
          kaympus: "हर आँकड़ा लाइव है और क्लिक करने पर सीधे उन रिकॉर्ड तक ले जाता है जिनसे वह बना है। डैशबोर्ड पर कुछ भी बंद गली नहीं है।",
        },
      },
      "admin-people": {
        what: "आपके स्कूल में एक छात्र का पूरा सफ़र, एक ही जगह — उस दिन से जब कोई अनजान व्यक्ति पहली बार एडमिशन के बारे में फ़ोन करता है, उस दिन तक जब आप ट्रांसफ़र सर्टिफ़िकेट सौंपते हैं। स्टाफ़ रिकॉर्ड भी इसी तरह चलते हैं।",
        provides: [
          "हर बच्चे का एक ही रिकॉर्ड जिससे बाक़ी सब जुड़ा रहता है, इसलिए कोई नाम दोबारा नहीं टाइप होता",
          "पूछताछ और एडमिशन एक पाइपलाइन की तरह, कुछ भी किसी के निजी इनबॉक्स में अटका नहीं रहता",
          "शिक्षक और ग़ैर-शैक्षणिक स्टाफ़ के रिकॉर्ड, जो उपस्थिति, पेरोल और समय-सारणी तीनों को चलाते हैं",
        ],
        helps: [
          "छात्र का सेक्शन एक जगह बदलिए, और समय-सारणी, फीस योजना, रिपोर्ट कार्ड और अभिभावक ऐप सब अपने आप बदल जाते हैं",
          "एक ही सूची तीन रजिस्टरों में रखना बंद, और तीनों के अलग-अलग निकलने की परेशानी भी",
          "एडमिशन का मौसम इस बात पर निर्भर रहना बंद कर देता है कि किस क्लर्क को कौन सी पूछताछ याद है",
        ],
        different: {
          others: "एडमिशन आमतौर पर अपनी अलग छात्र सूची वाला अलग मॉड्यूल होता है, इसलिए दाख़िल हुए बच्चे को हाथ से दोबारा दर्ज करना पड़ता है, और जिस पूछताछ से बात शुरू हुई थी वह बदलते ही ग़ायब हो जाती है।",
          kaympus: "पूछताछ ही आवेदन बनती है, आवेदन ही छात्र बनता है, और पूरे रास्ते वह एक ही रिकॉर्ड रहता है। कुछ दोबारा नहीं टाइप होता और पूरा सिलसिला बना रहता है।",
        },
      },
      "admin-academics": {
        what: "वह सब जो समय-सारणी पर चलता है: कौन क्या पढ़ाता है, कौन उपस्थित था, क्या गृहकार्य दिया गया, किसकी परीक्षा हुई, और अंत में रिपोर्ट कार्ड पर क्या जाता है।",
        provides: [
          "समय-सारणी जो शिक्षकों की असली उपलब्धता पर बनती है, ख़ाली ख़ानों पर नहीं",
          "रोज़ाना उपस्थिति जो उसी दिन अभिभावक तक पहुँचती है जिस दिन दर्ज होती है",
          "रिपोर्ट कार्ड आपके स्कूल के प्रारूप में छपते हैं, वेंडर के प्रारूप में नहीं",
        ],
        helps: [
          "अभिभावकों को लगातार ग़ैरहाज़िरी की ख़बर दूसरे हफ़्ते मिलती है, सत्र के अंत की बैठक में नहीं",
          "रिपोर्ट कार्ड का मौसम तीन हफ़्ते स्प्रेडशीट जोड़ने का काम नहीं रह जाता",
          "शिक्षक द्वारा अंक भरना आख़िरी बार होता है जब उन नंबरों को कोई इंसान छूता है",
        ],
        different: {
          others: "उपस्थिति, परीक्षा और रिपोर्ट कार्ड अक्सर तीन अलग मॉड्यूल होते हैं जो निर्यात-आयात से डेटा बदलते हैं, यही वजह है कि ज़्यादातर स्कूलों में रिपोर्ट कार्ड की तारीख़ एक संकट बन जाती है।",
          kaympus: "कक्षा में भरे गए अंक पहले से रिपोर्ट कार्ड पर होते हैं। बनाने का चरण बस छपाई है, डेटा इकट्ठा करने की कवायद नहीं।",
        },
      },
      "admin-finance": {
        what: "आया हुआ पैसा और गया हुआ पैसा। एक तरफ़ फीस योजना और वसूली, दूसरी तरफ़ स्टाफ़ का वेतन और वैधानिक अनुपालन — और दोनों वही लोग-रिकॉर्ड पढ़ते हैं जो बाक़ी स्कूल इस्तेमाल करता है।",
        provides: [
          "किस्तों, भाई-बहन और स्टाफ़ रियायत, और नियम से लगने वाले विलंब शुल्क के साथ फीस योजनाएँ",
          "UPI, कार्ड और नेट बैंकिंग से ऑनलाइन भुगतान, नक़द वाली उसी खाता-बही में मिलान के साथ",
          "मासिक पेरोल, जिसमें वैधानिक कटौतियाँ गिनी जाती हैं, टाइप नहीं की जातीं",
        ],
        helps: [
          "दिन की कुल वसूली शाम 5 बजे ही सही होती है, किसी के रसीद बुक जोड़ने के बाद नहीं",
          "अभिभावक रात 11 बजे फ़ोन से भुगतान करते हैं, और दफ़्तर खुलने से पहले उसे दिख जाता है",
          "वेतन का दिन एक थके हुए व्यक्ति की दो दिन की स्प्रेडशीट मशक़्क़त नहीं रह जाता",
        ],
        different: {
          others: "फीस और पेरोल आमतौर पर अलग-अलग उत्पाद के रूप में बनते और बिकते हैं, अक्सर अलग टीमों के हाथों, और ऑनलाइन भुगतान का काउंटर वसूली से मिलान हर महीने हाथ से करना पड़ता है।",
          kaympus: "दोनों एक ही सिस्टम में, उन्हीं स्टाफ़ और छात्र रिकॉर्ड पर चलते हैं, और ऑनलाइन भुगतान जमा होते ही उसी खाता-बही में पहुँचता है जिसमें नक़द रसीद।",
        },
      },
      "admin-operations": {
        what: "स्कूल के वे हिस्से जो पढ़ाई नहीं हैं (पुस्तकालय, छात्रावास, बसें, खेल का मैदान), और स्कूल के अपने लोगों से बात करने के हर तरीक़े — उस सार्वजनिक वेबसाइट समेत जिससे अभिभावक आपको आँकते हैं।",
        provides: [
          "पुस्तकालय, छात्रावास, परिवहन और खेल, बाक़ी सब की तरह उसी सिस्टम के अंदर",
          "दफ़्तर, शिक्षकों और अभिभावकों के बीच संदेश, जिसमें किसी का निजी नंबर कभी सामने नहीं आता",
          "आपकी सार्वजनिक स्कूल वेबसाइट और कस्टम डोमेन, स्कूल के अपने हाथ में",
        ],
        helps: [
          "सुबह 8 बजे घोषित छुट्टी 8:05 तक हर अभिभावक के फ़ोन पर होती है, किसी WhatsApp फ़ॉरवर्ड पर नहीं",
          "शिकायत इस बात पर निर्भर रहना बंद कर देती है कि फ़ोन किस क्लर्क ने उठाया था",
          "वेबसाइट वह चीज़ नहीं रह जाती जिसे बदलवाने के लिए साल में दो बार एजेंसी को पैसे देने पड़ें",
        ],
        different: {
          others: "पुस्तकालय, छात्रावास और परिवहन आमतौर पर अलग से ख़रीदे जाने वाले ऐड-ऑन होते हैं, हर एक का अपना लॉगिन और छात्र सूची की अपनी नक़ल, और स्कूल की वेबसाइट तो पूरी तरह अलग ख़रीद होती है।",
          kaympus: "ये शामिल हैं और वही छात्र रिकॉर्ड पढ़ते हैं, इसलिए छात्रावास में रहने वाला और बस से आने वाला वही बच्चा है जिसे आपने दाख़िल किया था, और वेबसाइट भी एक मॉड्यूल भर है।",
        },
      },
      "teacher-day": {
        what: "शिक्षक की अपनी स्क्रीन, सिर्फ़ उन्हीं कक्षाओं तक सीमित जो वे सचमुच पढ़ाते हैं। यह आज पर खुलती है: कौन से पीरियड, कौन से सेक्शन, कौन ग़ैरहाज़िर है।",
        provides: [
          "आज के पीरियड और कक्षाएँ उसी स्क्रीन पर जहाँ वे पहुँचते हैं, कहीं जाने की ज़रूरत नहीं",
          "उनके अपने सेक्शन और छात्र सूचियाँ, और किसी और की नहीं",
          "पूरी कक्षा की उपस्थिति एक मिनट से कम में दर्ज",
        ],
        helps: [
          "उपस्थिति कक्षा में ही दर्ज हो जाती है, इसलिए रजिस्टर दोपहर तक पूरा होता है, शाम तक नहीं",
          "शिक्षक किसी सूची के लिए दफ़्तर की खिड़की पर क़तार लगाना बंद कर देते हैं",
          "नए शिक्षक पहले ही दिन काम शुरू कर देते हैं, क्योंकि अपनी स्क्रीन के अलावा सीखने को कुछ है ही नहीं",
        ],
        different: {
          others: "कई ERP शिक्षक को वही एडमिन पैनल दे देते हैं जिसमें कुछ बटन छिपा दिए गए हों, इसलिए उन्हें अपनी दो ज़रूरी स्क्रीन तक पहुँचने के लिए प्रधानाचार्य के लिए बना मेन्यू खंगालना पड़ता है।",
          kaympus: "शिक्षक वाला हिस्सा अपने आप में बना है। इसमें उनकी कक्षाएँ, उनके पीरियड और उनका बाक़ी काम दिखता है, और भटकने के लिए और कुछ है ही नहीं।",
        },
      },
      "teacher-classroom": {
        what: "शिक्षक जो काम देते हैं और जिसका मूल्यांकन करते हैं (गृहकार्य, असाइनमेंट, क्लास टेस्ट और परीक्षाएँ) — देने से लेकर परिणाम प्रकाशित करने तक।",
        provides: [
          "गृहकार्य और असाइनमेंट जो पोस्ट होते ही अभिभावक तक पहुँच जाते हैं",
          "क्लास टेस्ट और अंक सीधे दर्ज, बीच में कोई स्प्रेडशीट नहीं",
          "परीक्षा कार्यक्रम और परिणाम उन्हीं छात्रों तक प्रकाशित जिन्होंने परीक्षा दी",
        ],
        helps: [
          "\"उन्होंने कहा था गृहकार्य नहीं है\" अब कोई बहस का विषय नहीं रह जाता",
          "अंक एक ही बार दर्ज होते हैं, उसी व्यक्ति द्वारा जिसने कॉपी जाँची, और दोबारा कभी नहीं टाइप होते",
          "भ्रमण से पहले सहमति पर्चियाँ इकट्ठी करना हफ़्ते भर की याददिहानी नहीं रह जाता",
        ],
        different: {
          others: "गृहकार्य अक्सर एकतरफ़ा सूचना पट्ट भर होता है, और अंक एक टेम्पलेट स्प्रेडशीट पर इकट्ठे किए जाते हैं जिन्हें दफ़्तर फिर परीक्षा मॉड्यूल में दोबारा भरता है।",
          kaympus: "शिक्षक जो पोस्ट करते हैं वही अभिभावक देखते हैं, और शिक्षक जो दर्ज करते हैं वही रिपोर्ट कार्ड पर आता है। बीच में दोबारा भरने का कोई चरण नहीं जहाँ चीज़ें खो सकें।",
        },
      },
      "teacher-app": {
        what: "शिक्षकों के लिए असली Android और iOS ऐप, ताकि कक्षा में होने वाला काम कक्षा में ही दर्ज हो, स्टाफ़ रूम तक याद रखने की ज़रूरत न पड़े।",
        provides: [
          "दोनों स्टोर पर असली ऐप, वेबसाइट की छोटी की हुई नक़ल नहीं",
          "उपस्थिति कक्षा के सामने खड़े-खड़े, हाथ के फ़ोन पर दर्ज",
          "अभिभावक को जवाब, बिना अपना निजी नंबर दिए",
        ],
        helps: [
          "उपस्थिति का डेटा तब आता है जब वह अब भी सही है, शाम 4 बजे याद से जोड़ा हुआ नहीं",
          "शिक्षक कमरों के बीच काग़ज़ी रजिस्टर ढोना बंद कर देते हैं",
          "अभिभावकों के संदेशों का जवाब उसी दिन मिल जाता है, क्योंकि जवाब देने में तीस सेकंड लगते हैं",
        ],
        different: {
          others: "शिक्षक ऐप अक्सर अलग से ख़रीदा जाने वाला लाइसेंस होता है, और जहाँ होता भी है वहाँ प्रायः मोबाइल वेबसाइट का आवरण भर होता है, जो नेटवर्क टूटते ही बेकार हो जाता है।",
          kaympus: "ऐप सिस्टम के साथ बिना किसी अतिरिक्त शुल्क के आता है और फ़ोन के लिए ही बना है, इसलिए कक्षा की हाज़िरी मैदान पर भी वैसे ही लगती है जैसे दफ़्तर के wifi पर।",
        },
      },
      "teacher-self": {
        what: "शिक्षक के खाते का नौकरी वाला पहलू: उनकी छुट्टी, उनका वेतन, और वे रिपोर्ट जिनके लिए वे कक्षा अध्यापक के नाते ज़िम्मेदार हैं।",
        provides: [
          "ऐप से छुट्टी का आवेदन, आवेदन करने से पहले ही मौजूदा बैलेंस सामने",
          "पेस्लिप और वेतन का पूरा इतिहास, जिसे वे ख़ुद डाउनलोड कर सकते हैं",
          "अपने ही सेक्शन की उपस्थिति और प्रदर्शन रिपोर्ट",
        ],
        helps: [
          "HR डेस्क को महीने में बीस बार \"मेरी कितनी आकस्मिक छुट्टी बची है\" सुनना बंद हो जाता है",
          "पेस्लिप की माँग लेखाकार को WhatsApp संदेश के रूप में आना बंद हो जाती है",
          "कक्षा अध्यापक अभिभावक के सवाल का जवाब दफ़्तर से पूछे बिना दे सकते हैं",
        ],
        different: {
          others: "शिक्षकों का HR आमतौर पर उसी पेरोल सॉफ़्टवेयर में रहता है जो लेखा विभाग ने ख़रीदा था, जिसका लॉगिन शिक्षकों के पास होता ही नहीं, इसलिए हर माँग किसी व्यक्ति से होकर गुज़रती है।",
          kaympus: "छुट्टी, पेस्लिप और पढ़ाने का रिकॉर्ड एक ही लॉगिन के पीछे हैं, क्योंकि ये सब एक ही कर्मचारी के बारे में तथ्य हैं।",
        },
      },
      "staff-workspace": {
        what: "वह खाता जो ग़ैर-शैक्षणिक कर्मचारी को मिलता है: लेखाकार, पुस्तकालयाध्यक्ष, वार्डन, दफ़्तर का क्लर्क। सिस्टम वही, पर दिखता सिर्फ़ स्कूल का वह हिस्सा है जो उस व्यक्ति का काम है।",
        provides: [
          "काम के हिसाब से बना होम स्क्रीन, प्रधानाचार्य वाले की काट-छाँट की हुई नक़ल नहीं",
          "सिर्फ़ वही मॉड्यूल जिनकी अनुमति उस भूमिका को सचमुच दी गई है, बाक़ी वाक़ई मौजूद ही नहीं",
          "टिकट उठाए और समाधान तक ट्रैक किए जाते हैं, याद रखे नहीं जाते",
        ],
        helps: [
          "आप दफ़्तर के क्लर्क को लॉगिन दे सकते हैं, वेतन रजिस्टर दिए बिना",
          "स्टाफ़ ऐसा मेन्यू सीखना बंद कर देता है जिसका 80% उनके काम का है ही नहीं",
          "नए कर्मचारी को काम सौंपना अनुमति देने भर की बात है, हफ़्ते भर साथ बिठाने की नहीं",
        ],
        different: {
          others: "ग़ैर-शैक्षणिक स्टाफ़ को अक्सर एक साझा दफ़्तरी लॉगिन थमा दिया जाता है, क्योंकि अनुमति व्यवस्था असल में सिर्फ़ एडमिन और शिक्षक के बीच फ़र्क़ करती है।",
          kaympus: "अनुमतियाँ हर व्यक्ति और हर मॉड्यूल के हिसाब से दी जाती हैं, इसलिए हर कर्मचारी का अपना खाता और अपनी जवाबदेही का सिलसिला होता है।",
        },
      },
      "staff-app": {
        what: "स्टाफ़ का फ़ोन ऐप: उन लोगों के लिए उपस्थिति, छुट्टी और वेतन जो शायद ही कभी मेज़ पर बैठते हैं।",
        provides: [
          "बायोमेट्रिक पंच-इन, जियोफ़ेंस के साथ, ताकि वह सिर्फ़ कैंपस में ही गिने",
          "छुट्टी का आवेदन और बैलेंस की जाँच, HR तक गए बिना",
          "इस महीने की पेस्लिप, PDF में डाउनलोड",
        ],
        helps: [
          "उन कर्मचारियों की भी उपस्थिति दर्ज होती है जो अपनी पूरी पाली में कभी कंप्यूटर पर नहीं बैठते",
          "HR डेस्क को उन सवालों के लिए टोका जाना बंद हो जाता है जिनका जवाब कर्मचारी ख़ुद निकाल सकता है",
          "पेस्लिप बाँटना हर महीने की छपाई का काम नहीं रह जाता",
        ],
        different: {
          others: "स्टाफ़ उपस्थिति का मतलब आमतौर पर एक बायोमेट्रिक मशीन और महीने का निर्यात होता है, जिसमें कर्मचारी के लिए ख़ुद कुछ देखने की सुविधा होती ही नहीं।",
          kaympus: "फ़ोन ही मशीन है। पंच, छुट्टी का बैलेंस और पेस्लिप — तीनों कर्मचारी के हाथ में हैं, और रजिस्टर उसी समय अपडेट होता है।",
        },
      },
      "staff-frontdesk": {
        what: "फ्रंट ऑफ़िस: वह काउंटर जहाँ फीस ली जाती है, एडमिशन की पूछताछ आती है और अभिभावक सवाल लेकर पहुँचते हैं।",
        provides: [
          "फीस वसूली, रसीद उसी वक़्त छपती है जब पैसे लिए जाते हैं",
          "नक़द, चेक और ऑनलाइन भुगतान, सब एक ही काउंटर पर",
          "एडमिशन की पूछताछ और आवेदन, डेस्क पर ही दर्ज",
        ],
        helps: [
          "काउंटर उस कुल जोड़ के साथ बंद होता है जो सिस्टम से पहले ही मेल खाता है",
          "सोमवार को डेस्क पर ली गई पूछताछ तीसरे हफ़्ते भी ट्रैक पर रहती है",
          "जिस अभिभावक से वापस फ़ोन करने का वादा हुआ था, उसे वाक़ई फ़ोन आता है",
        ],
        different: {
          others: "फीस काउंटर अक्सर एक अलग लेखा पैकेज होता है, इसलिए वह जो रसीद छापता है और स्कूल जो छात्र रिकॉर्ड रखता है, दोनों अलग-अलग सिस्टम में रहते हैं।",
          kaympus: "रसीद छात्र के अपने रिकॉर्ड पर जारी होती है, इसलिए अभिभावक के बिल्डिंग से निकलने से पहले ही उनके ऐप पर बकाया अपडेट हो जाता है।",
        },
      },
      "staff-facilities": {
        what: "वे सुविधाएँ जो स्कूल पढ़ाई के साथ-साथ चलाता है (पुस्तकालय, छात्रावास, खेल कार्यक्रम और बसें) — हर एक ऐसी कि जो उसे चलाता है, वही उसे संभाल सके।",
        provides: [
          "किताबों की सूची, चालू इश्यू और वापसी डेस्क के साथ",
          "छात्रावास के कमरे, बिस्तर आवंटन और भरा-ख़ाली, सब एक नज़र में",
          "परिवहन के रूट और स्टॉप, उन बच्चों से जुड़े हुए जो उन्हें इस्तेमाल करते हैं",
        ],
        helps: [
          "पुस्तकालयाध्यक्ष वह समानांतर रजिस्टर रखना बंद कर देते हैं जिसकी जगह लेने के लिए ERP लाया गया था",
          "वार्डन \"कौन सा बिस्तर ख़ाली है\" का जवाब पूरा ब्लॉक घूमे बिना दे सकते हैं",
          "सुविधा कर्मचारियों को अपने काम का औज़ार मिलता है, किसी और के काम का फ़ॉर्म नहीं",
        ],
        different: {
          others: "ये वही जाने-पहचाने ऐड-ऑन हैं जिनके लिए अलग पैसे लगते हैं — हर एक का अपना लॉगिन, छात्र सूची की अपनी नक़ल, और उपस्थिति या फीस से कोई नाता नहीं।",
          kaympus: "ये शामिल हैं और वही छात्र रिकॉर्ड पढ़ते हैं, इसलिए पुस्तकालय का जुर्माना और छात्रावास का बिस्तर उसी बच्चे के हैं जिसे स्कूल पहले से जानता है।",
        },
      },
      "student-home": {
        what: "जो परिवार देखता है। वही स्कूल, एक बच्चे तक छाना हुआ — छात्र के लिए उसके अपने लॉगिन पर, और अभिभावकों के लिए उनके अपने लॉगिन पर।",
        provides: [
          "एक होम स्क्रीन छात्र के लिए बना, और एक अभिभावकों के लिए",
          "उपस्थिति का रिकॉर्ड, दिन-ब-दिन, सत्र के अंत के प्रतिशत के रूप में नहीं",
          "सूचनाएँ, कार्यक्रम और छुट्टियों का कैलेंडर, जैसे ही स्कूल उन्हें प्रकाशित करता है",
        ],
        helps: [
          "अभिभावक उपस्थिति पूछने के लिए दफ़्तर फ़ोन करना बंद कर देते हैं, क्योंकि वे ख़ुद देख सकते हैं",
          "लगातार ग़ैरहाज़िरी अभिभावक को दूसरे हफ़्ते दिख जाती है, सत्र के अंत की बैठक में नहीं",
          "स्कूल हर परिवार तक एक साथ बात पहुँचाता है, बिना किसी WhatsApp ग्रुप को सँभाले",
        ],
        different: {
          others: "अभिभावक वाला हिस्सा आमतौर पर सबसे आख़िर में बनता है और सबसे पहले काटा जाता है: सिर्फ़ पढ़ने लायक़ सारांश, रात की सिंक से अपडेट होता हुआ, ऐड-ऑन के तौर पर बिकता हुआ।",
          kaympus: "परिवार पोर्टल सिस्टम के साथ आता है और लाइव पढ़ता है। अभिभावक 9:00 बजे वही देखते हैं जो दफ़्तर ने 8:55 पर भरा था — बीच में सफ़ाई देने के लिए कोई सिंक विंडो नहीं।",
        },
      },
      "student-app": {
        what: "वह फ़ोन ऐप जिसे ज़्यादातर अभिभावक सचमुच इस्तेमाल करेंगे, क्योंकि कामकाजी अभिभावक के लिए ऐप ही स्कूल है।",
        provides: [
          "उपस्थिति की सूचना उसी दिन, सत्र के अंत में नहीं",
          "गृहकार्य और टेस्ट की सूचनाएँ, जैसे ही शिक्षक उन्हें पोस्ट करते हैं",
          "कक्षा की बातचीत और स्कूल के सर्कुलर, सब एक ही जगह",
        ],
        helps: [
          "अभिभावक को पता चल जाता है कि बच्चा आज स्कूल नहीं गया, जब \"आज\" का अब भी मतलब है",
          "गृहकार्य को लेकर बहस ख़त्म, क्योंकि गृहकार्य अभिभावक के फ़ोन पर है",
          "सर्कुलर वह काग़ज़ नहीं रह जाते जो कभी बस्ते से बाहर ही नहीं निकला",
        ],
        different: {
          others: "अभिभावक ऐप आमतौर पर हर छात्र पर अलग लाइसेंस होता है, और अक्सर वह सारांश दिखाता है जो सुबह तक सही था।",
          kaympus: "यह हर परिवार के लिए शामिल है और लाइव डेटा पढ़ता है। न हर अभिभावक पर कोई शुल्क, न रात भर का इंतज़ार।",
        },
      },
      "student-academics": {
        what: "परिवार के खाते का पढ़ाई वाला पहलू: क्या दिया गया है, क्या आने वाला है, और वह कैसा रहा।",
        provides: [
          "गृहकार्य और असाइनमेंट, अपनी तय तारीख़ों के साथ, जैसे शिक्षक ने पोस्ट किए",
          "परिणाम, उसी क्षण जब स्कूल उन्हें प्रकाशित करता है",
          "रिपोर्ट कार्ड PDF में डाउनलोड, शनिवार को लेने जाने की ज़रूरत नहीं",
        ],
        helps: [
          "अभिभावक गृहकार्य में मदद कर पाते हैं, क्योंकि उन्हें पता है कि गृहकार्य क्या है",
          "परीक्षा का हफ़्ता पहले से तैयारी का होता है, अचानक पता चलने वाला नहीं",
          "रिपोर्ट कार्ड की दूसरी प्रति के लिए कोई दफ़्तर के बाहर क़तार नहीं लगाता",
        ],
        different: {
          others: "परिणाम आमतौर पर छपी हुई शीट या हफ़्तों बाद भेजी गई PDF के रूप में परिवार तक पहुँचते हैं, और गृहकार्य अगर साझा होता भी है तो कक्षा के WhatsApp ग्रुप में।",
          kaympus: "यह उसी रिकॉर्ड से आता है जो शिक्षक ने भरा था, इसलिए स्कूल जैसे ही प्रकाशित करने का फ़ैसला करता है, परिवार को वह दिख जाता है — हर बार उसी जगह।",
        },
      },
      "student-fees": {
        what: "परिवार के खाते का फीस वाला पहलू: कितना बकाया है, कितना जमा हुआ है, और उसे स्कूल आए बिना चुकाने का रास्ता।",
        provides: [
          "क्या देय है, क्या जमा है और अगली किस्त कब है — सब एक ही जगह",
          "UPI, कार्ड या नेट बैंकिंग से ऑनलाइन भुगतान",
          "देय तारीख़ से पहले याद दिलाना, बाद में तक़ाज़ा करना नहीं",
        ],
        helps: [
          "वसूली बेहतर होती है, क्योंकि भुगतान में एक मिनट लगता है और स्कूल आने की ज़रूरत नहीं",
          "खो गई रसीदों के बदले दूसरी रसीद जारी करना दफ़्तर के लिए बंद हो जाता है",
          "फीस की याद दिलाना अभिभावक को किया गया असहज फ़ोन कॉल नहीं रह जाता",
        ],
        different: {
          others: "ऑनलाइन फीस भुगतान अक्सर एक पेमेंट गेटवे पेज से जोड़ दिया जाता है जो छात्र को जानता ही नहीं, इसलिए दफ़्तर हर हफ़्ते लेन-देन का मिलान हाथ से करता है।",
          kaympus: "भुगतान बच्चे की अपनी फीस योजना पर होता है और उसी खाता-बही में जाता है जिसमें नक़द रसीद, इसलिए जमा होते ही बकाया सही हो जाता है।",
        },
      },
    },
  },

  cta: {
    headingLine1: "अपने स्कूल का कामकाज",
    headingLine2: "आसान बनाने के लिए तैयार हैं?",
    bodyLine1: "सिस्टम सँभालना छोड़िए। विकास पर ध्यान दीजिए।",
    bodyLine2: "आज ही अपना फ्री डेमो बुक कीजिए।",
    btnDemo: "फ्री डेमो शुरू करें",
    btnSales: "सेल्स टीम से बात करें",
    badgeNoCard: "क्रेडिट कार्ड की ज़रूरत नहीं",
    badgeTrial: "15 दिन मुफ़्त पहुँच",
    badgeCancel: "कभी भी बंद करें",
    photoAlt: "स्कूल प्रशासक",
  },

  pricing: {
    /* Hindi puts the verb last, so the accent word lands mid-sentence and the
       trailing "में निवेश कीजिए" has no English counterpart. */
    headingBefore: "अपने स्कूल के",
    headingAccent: "भविष्य",
    headingAfter: "में निवेश कीजिए",
    subtitle:
      "कोई भी प्लान लेते ही आपके स्कूल का अपना इंस्टेंस बन जाता है। हर प्लान में पूरा ERP सुइट शामिल है — कोई फ़ीचर छिपाकर नहीं रखा जाता।",
    cycles: [
      { label: "मासिक", suffix: "/माह" },
      { label: "3 महीने", suffix: "/3 महीने" },
      { label: "6 महीने", suffix: "/6 महीने" },
      { label: "वार्षिक", suffix: "/वर्ष" },
    ],
    loading: "प्लान लोड हो रहे हैं...",
    errorNoPlans: "कोई प्लान उपलब्ध नहीं है",
    errorLoad: "कीमत लोड नहीं हो सकी",
    viewAllPlans: "सभी प्लान देखें",
    mostPopular: "सबसे लोकप्रिय",
    recommended: "अनुशंसित",
    choosePlan: "यह प्लान चुनें",
    whatsIncluded: "इसमें क्या शामिल है",
    save: "{percent}% बचत",
    trustNoCard: "क्रेडिट कार्ड की ज़रूरत नहीं · अपना फ्री ट्रायल लीजिए",
    customPlanBefore: "कस्टम प्लान चाहिए?",
    customPlanLink: "हमारी सेल्स टीम से संपर्क करें",
    faqHeading: "कीमत से जुड़े सवाल, <em>और उनके जवाब।</em>",
    faqLabel: "बिलिंग",
    faqItems: [
      {
        question: "क्या मैं बाद में प्लान बदल सकता हूँ?",
        answer: "बिल्कुल। आप कभी भी अपना प्लान बढ़ा या घटा सकते हैं। आनुपातिक राशि अगले बिलिंग चक्र में अपने आप जुड़ जाएगी।",
      },
      {
        question: "क्या कोई सेटअप शुल्क है?",
        answer: "नहीं, कोई छिपा हुआ सेटअप शुल्क या ऑनबोर्डिंग लागत नहीं है। आप सिर्फ़ तय मासिक या वार्षिक राशि देते हैं।",
      },
      {
        question: "डेटा माइग्रेशन कैसे होता है?",
        answer: "हमारी ऑनबोर्डिंग टीम आपके मौजूदा छात्र, स्टाफ़ और वित्तीय रिकॉर्ड सुरक्षित Excel/CSV आयात के ज़रिए बिना किसी शुल्क के ले आती है।",
      },
    ],
  },

  plans: {
    headingBefore: "अपने स्कूल के",
    headingAccent: "भविष्य",
    headingAfter: "में निवेश कीजिए",
    subtitle:
      "प्लान लेते ही आपके स्कूल का अपना इंस्टेंस अपने आप बन जाता है। पहले ही दिन से Kaympus पर सब कुछ सँभालिए।",
    trust: ["99.9% अपटाइम SLA", "5 मिनट में सेटअप", "क्रेडिट कार्ड की ज़रूरत नहीं"],
    errorLoad: "प्लान लोड नहीं हो सके",
    errorConnect: "सर्वर से संपर्क नहीं हो सका",
    retry: "दोबारा कोशिश करें",
    faqHeading: "प्लान से जुड़े सवाल, <em>और उनके जवाब।</em>",
    faqLabel: "प्लान चुनना",
    faqItems: [
      {
        question: "हमें किस प्लान से शुरू करना चाहिए?",
        answer: "वह प्लान चुनिए जो आपकी मौजूदा छात्र संख्या और पहले दिन से ज़रूरी मॉड्यूल कवर करता हो। आप बाद में बिना कोई डेटा खोए प्लान बढ़ा या घटा सकते हैं, इसलिए ज़्यादातर स्कूल अगले साल के अनुमान के बजाय आज के आकार के हिसाब से शुरुआत करते हैं।",
      },
      {
        question: "जब हमारी छात्र संख्या तय सीमा पार कर जाए तो क्या होगा?",
        answer: "परेशानी बनने से पहले ही हम आपको बता देंगे और अगले स्तर पर ले जाएँगे। सत्र के बीच कुछ भी बंद नहीं होता और कोई रिकॉर्ड लॉक नहीं किया जाता।",
      },
      {
        question: "क्या हम प्रतिबद्ध होने से पहले Kaympus आज़मा सकते हैं?",
        answer: "हाँ। बिना क्रेडिट कार्ड के फ्री ट्रायल शुरू कीजिए, एक-दो कक्षाओं का असली डेटा डालिए, और पैसे देने से पहले देखिए कि यह आपके कामकाज में कैसे बैठता है।",
      },
      {
        question: "क्या सभी प्लान में सपोर्ट और ऑनबोर्डिंग शामिल है?",
        answer: "हाँ। हर प्लान में सहायता सहित डेटा माइग्रेशन, स्टाफ़ प्रशिक्षण, प्रोडक्ट के भीतर हेल्पडेस्क, और WhatsApp तथा फ़ोन सपोर्ट शामिल है।",
      },
    ],
  },

  contact: {
    heading: "Kaympus को\n<em>काम करते देखिए</em>",
    subtitle:
      "आपके स्कूल के आकार और ज़रूरतों के हिसाब से 30 मिनट का निजी वॉकथ्रू लीजिए। हमारे विशेषज्ञ आपको ठीक-ठीक दिखाएँगे कि Kaympus आपकी सबसे बड़ी परेशानियाँ कैसे हल करता है।",
    benefits: [
      "सभी विशेषताओं का लाइव वॉकथ्रू",
      "आपके स्कूल के आकार के हिसाब से सेटअप",
      "प्रोडक्ट विशेषज्ञ से सवाल-जवाब",
      "क्रेडिट कार्ड की ज़रूरत नहीं",
    ],
    typeDemo: "फ्री डेमो",
    typeEnquiry: "पूछताछ",
    successTitle: "आपका अनुरोध मिल गया!",
    successBefore: "धन्यवाद,",
    successAfter: "! हमारी टीम 24 घंटे के भीतर आपसे संपर्क करके आपका निजी डेमो तय करेगी।",
    successFallbackName: "जी",
    submitAnother: "एक और अनुरोध भेजें",
    labelName: "आपका नाम",
    labelSchool: "स्कूल का नाम",
    labelEmail: "ईमेल पता",
    labelPhone: "फ़ोन नंबर",
    labelMessage: "आपका संदेश",
    labelStudents: "छात्रों की संख्या",
    placeholderName: "जैसे राजेश कुमार",
    placeholderSchool: "जैसे DAV पब्लिक स्कूल",
    placeholderEmail: "you@school.com",
    placeholderPhone: "+91 98765 43210",
    placeholderMessage: "बताइए, आप Kaympus के बारे में क्या जानना चाहेंगे...",
    placeholderStudents: "सीमा चुनिए...",
    studentRanges: [
      "200 से कम छात्र",
      "200 - 500 छात्र",
      "500 - 1,000 छात्र",
      "1,000 - 2,000 छात्र",
      "2,000+ छात्र",
    ],
    submitting: "भेजा जा रहा है...",
    submitEnquiry: "मेरी पूछताछ भेजें",
    submitDemo: "मेरा फ्री डेमो बुक करें",
    errorSubmit: "आपका अनुरोध नहीं भेजा जा सका। कृपया दोबारा कोशिश करें।",
    errorGeneric: "कुछ गड़बड़ हो गई। कृपया दोबारा कोशिश करें।",
  },

  systemStatus: {
    banner: "सभी सिस्टम चालू • 99.98% अपटाइम",
    heading: "Kaympus <em>सिस्टम स्थिति</em>",
    subtitle:
      "रियल-टाइम परिचालन स्थिति, सेवा प्रतिक्रिया समय के आँकड़े, और सिस्टम रखरखाव का ब्यौरा।",
    currentHeading: "मौजूदा सेवा स्थिति",
    currentSubtitle: "सभी क्लाउड नोड पर लाइव निगरानी",
    liveSync: "लाइव ऑटो-सिंक",
    latencyLabel: "प्रतिक्रिया समय:",
    uptimeLabel: "अपटाइम (30 दिन)",
    statusOperational: "चालू",
    logsHeading: "हाल के सिस्टम लॉग और रखरखाव",
    services: [
      "कोर ERP क्लाउड API और वेब डैशबोर्ड",
      "SMS और पुश नोटिफ़िकेशन ब्रॉडकास्ट गेटवे",
      "ऑनलाइन फीस पेमेंट गेटवे इंटीग्रेशन (UPI/कार्ड)",
      "CBSE रिपोर्ट कार्ड और PDF जनरेशन इंजन",
      "GPS बस फ़्लीट ट्रैकिंग और लाइव लोकेशन स्ट्रीम",
      "बायोमेट्रिक और RFID गेट उपस्थिति सिंक",
      "अभिभावक और शिक्षक Android/iOS मोबाइल ऐप API",
      "स्वचालित ऑफ़-साइट दैनिक डेटाबेस बैकअप",
    ],
    incidents: [
      {
        date: "2 अगस्त 2026",
        title: "निर्धारित रखरखाव: पेमेंट गेटवे अनुकूलन",
        status: "पूर्ण",
        desc: "पेमेंट वेबहुक के लिए नियमित डेटाबेस इंडेक्स अनुकूलन। सभी सेवाएँ पूरी तरह चालू रहीं, कोई डाउनटाइम नहीं।",
      },
      {
        date: "18 जुलाई 2026",
        title: "SMS और नोटिफ़िकेशन क़तार वितरण अनुकूलन",
        status: "सुलझाया गया",
        desc: "सुबह के व्यस्त घंटों में SMS ब्रॉडकास्ट वितरण में 6 मिनट की मामूली देरी। बैकअप नोड ने क़तार अपने आप सुलझा दी।",
      },
      {
        date: "24 जून 2026",
        title: "क्लाउड इंफ़्रास्ट्रक्चर क्षमता विस्तार",
        status: "पूर्ण",
        desc: "देश भर में CBSE टर्म 1 के रिपोर्ट कार्ड परिणाम सुचारू रूप से प्रकाशित करने के लिए सर्वर क्षमता 300% बढ़ाई गई।",
      },
    ],
  },

  caseStudies: {
    badge: "प्रमाणित नतीजे और सफलता की कहानियाँ",
    heading: "अग्रणी स्कूल <em>Kaympus के साथ कैसे आगे बढ़े</em>",
    subtitle:
      "500+ स्कूलों के असली केस स्टडी देखिए, जिन्होंने फीस वसूली डिजिटल की, CBSE रिपोर्ट कार्ड स्वचालित किए, और अभिभावकों का भरोसा बढ़ाया।",
    topMetrics: [
      "समय पर फीस वसूली",
      "प्रति सत्र बचा शिक्षक समय",
      "ऐप पर अभिभावक पठन दर",
      "बदले हुए स्कूल",
    ],
    keyImpact: "मुख्य प्रभाव के आँकड़े",
    ctaBefore: "अपना स्कूल भी ऐसा बनाइए, जैसे",
    studies: [
      {
        location: "जयपुर, राजस्थान",
        students: "3,200 छात्र",
        badge: "फीस स्वचालन और UPI",
        title: "DAV पब्लिक स्कूल ने 100% फीस वसूली डिजिटल कैसे की और हर महीने 180+ घंटे कैसे बचाए",
        metricLabels: ["फीस वसूली की कुशलता", "काउंटर की क़तार में कमी", "डिजिटल रसीदें भेजी गईं"],
        summary: "Kaympus से पहले अभिभावक फीस के हफ़्तों में घंटों क़तार में लगते थे। स्वचालित UPI पेमेंट लिंक और तुरंत मिलने वाली डिजिटल रसीदों के बाद 98% अभिभावक अब 48 घंटों के भीतर ऑनलाइन फीस जमा कर देते हैं।",
        quote: "Kaympus ने हमारे प्रशासनिक कामकाज को पूरी तरह बदल दिया। अभिभावकों को UPI से ऑनलाइन भुगतान की सुविधा बहुत पसंद है, और हमारी लेखा टीम फीस का मिलान उसी समय कर लेती है।",
        author: "डॉ. राजेश शर्मा, प्रधानाचार्य",
      },
      {
        location: "अजमेर, राजस्थान",
        students: "2,800 छात्र",
        badge: "CBSE मार्कशीट",
        title: "2,800+ छात्रों के CBSE रिपोर्ट कार्ड और परीक्षा ग्रेडिंग का तुरंत स्वचालन",
        metricLabels: ["प्रति सत्र बचे शिक्षक घंटे", "मार्कशीट की शुद्धता", "रिपोर्ट छपाई की रफ़्तार"],
        summary: "टर्म 1 और टर्म 2 के अंक जोड़ने में शिक्षकों के पहले 2 हफ़्ते Excel में हाथ से भरने में लगते थे। Kaympus ने ग्रेड, उपस्थिति और सह-शैक्षिक मानदंड अपने आप गिनकर तुरंत छपने लायक़ PDF बना दीं।",
        quote: "हमारे शिक्षक पहले सप्ताहांत रिपोर्ट कार्ड हाथ से भरने में बिताते थे। Kaympus से पूरे सत्र के परिणाम 24 घंटे में प्रकाशित होकर अभिभावकों तक पहुँच गए।",
        author: "सि. टेरेसा डिसूज़ा, उप-प्रधानाचार्य",
      },
      {
        location: "जोधपुर, राजस्थान",
        students: "2,100 छात्र",
        badge: "SMS और ऐप सिंक",
        title: "रियल-टाइम SMS और मोबाइल ऐप अलर्ट से 99% अभिभावक जुड़ाव",
        metricLabels: ["रोज़ाना उपस्थिति सूचनाएँ", "सर्कुलर पठन दर", "SMS लागत में कमी"],
        summary: "पारंपरिक SMS सूचनाएँ अक्सर बिना पढ़े रह जाती थीं या DND फ़िल्टर में अटक जाती थीं। Kaympus ऐप की पुश सूचनाओं से तुरंत रिच-मीडिया अपडेट, गृहकार्य PDF और फीस रिमाइंडर भेजना संभव हुआ।",
        quote: "अभिभावकों को अब रोज़ाना उपस्थिति अपडेट और फीस रसीदें सीधे Kaympus ऐप में मिलती हैं। संवाद की खाई शून्य हो गई है।",
        author: "विक्रम राठौड़, निदेशक",
      },
      {
        location: "उदयपुर, राजस्थान",
        students: "1,950 छात्र",
        badge: "GPS फ़्लीट और RFID गेट",
        title: "रियल-टाइम GPS बस ट्रैकिंग और गेट RFID स्कैनर से 100% छात्र सुरक्षा",
        metricLabels: ["गेट RFID स्कैन दर", "अभिभावक GPS ऐप उपयोग", "बस रूट कुशलता"],
        summary: "अभिभावक बस के पहुँचने की जानकारी के लिए लगातार स्कूल दफ़्तर फ़ोन करते थे। Kaympus की लाइव फ़्लीट ट्रैकिंग और RFID गेट स्कैनर अभिभावकों को उनके बच्चे की सुरक्षा और पहुँचने के समय की रियल-टाइम जानकारी देते हैं।",
        quote: "स्कूल बस कहाँ है, इसकी चिंता अभिभावकों से पूरी तरह चली गई। Kaympus GPS ट्रैकिंग अब तक की सबसे बेहतरीन सुरक्षा सुविधा है जो हमने लगाई है।",
        author: "मीनाक्षी वर्मा, प्रधानाध्यापिका",
      },
    ],
  },

  videoTutorials: {
    badge: "वीडियो लाइब्रेरी और ट्यूटोरियल",
    heading: "Kaympus <em>मिनटों में सीखिए</em>",
    subtitle: "Kaympus की हर विशेषता के लिए चरण-दर-चरण वीडियो ट्यूटोरियल और मास्टरक्लास वॉकथ्रू देखिए।",
    featuredBadge: "विशेष मास्टरक्लास",
    featuredTitle: "Kaympus स्कूल ERP: पूरा परिचालन वॉकथ्रू",
    featuredDesc: "देखिए कि Kaympus फीस प्रबंधन, ऐप सूचनाएँ, CBSE मार्कशीट और लाइव GPS फ़्लीट ट्रैकिंग को एक ही आसान प्लेटफ़ॉर्म में कैसे जोड़ता है।",
    playerTitle: "Kaympus ERP वीडियो मास्टरक्लास",
    tutorialSuffix: "{duration} का ट्यूटोरियल",
    watchFull: "पूरा ट्यूटोरियल देखें ({duration})",
    watchNow: "अभी देखें",
    categories: [
      "प्लेटफ़ॉर्म मास्टरक्लास",
      "फीस प्रबंधन",
      "शिक्षण और परीक्षाएँ",
      "संचालन और फ़्लीट",
      "संवाद",
      "वित्त और HR",
    ],
    videos: [
      {
        title: "Kaympus स्कूल ERP: 5 मिनट में प्लेटफ़ॉर्म ओवरव्यू",
        desc: "Kaympus डैशबोर्ड, फीस स्वचालन, छात्र प्रोफ़ाइल और ऐप सूचनाओं का संक्षिप्त वॉकथ्रू।",
      },
      {
        title: "ऑनलाइन फीस गेटवे और स्वचालित डिजिटल रसीदों की सेटिंग",
        desc: "UPI/Paytm पेमेंट लिंक सेट करना, तुरंत रसीद बनाना और अपने आप रिमाइंडर भेजना सीखिए।",
      },
      {
        title: "CBSE और ICSE मार्कशीट जनरेटर और रिपोर्ट कार्ड छपाई",
        desc: "ग्रेडिंग स्केल तय करने, शिक्षकों के अंक अपलोड करने और एक साथ तुरंत PDF छापने का चरण-दर-चरण ट्यूटोरियल।",
      },
      {
        title: "स्मार्ट उपस्थिति और लाइव बस GPS ट्रैकिंग",
        desc: "गेट RFID स्कैनर और मोबाइल उपस्थिति अभिभावकों के लिए लाइव बस लोकेशन के साथ रियल-टाइम में कैसे सिंक होते हैं।",
      },
      {
        title: "ऐप सूचनाएँ, SMS और मैसेज टेम्पलेट सेटअप",
        desc: "सूचना टेम्पलेट सेट करने और अभिभावकों को Kaympus ऐप में तुरंत ब्रॉडकास्ट भेजने की गाइड।",
      },
      {
        title: "स्टाफ़ HR पेरोल और खाता मिलान मास्टरक्लास",
        desc: "शिक्षकों के वेतन, कटौतियों और अवकाश का स्वचालन, और बोर्ड बैठकों के लिए ऑडिट रिपोर्ट निर्यात करना।",
      },
    ],
  },

  legal: {
    lastUpdated: "अंतिम अद्यतन: 17 मार्च 2026",
    privacy: {
      lastUpdated: "अंतिम अद्यतन: 13 अक्टूबर 2026",
      heading: "हमारी गोपनीयता नीति,\n<em>सरल शब्दों में।</em>",
      subtitle:
        "Kaympus वेबसाइट और मोबाइल ऐप क्या जानकारी लेते हैं, क्यों लेते हैं, किसके साथ साझा करते हैं, कितने समय तक रखते हैं, और आप उसे कैसे मिटवा सकते हैं।",
      tocTitle: "इस पेज पर",
      contactCard: {
        title: "अपने डेटा के बारे में सवाल है?",
        body: "हमें लिखें, हम 30 दिनों के भीतर जवाब देंगे।",
      },
      closing: {
        heading: "अपना डेटा देखना, सुधारना या <em>मिटवाना</em> चाहते हैं?",
        body: "अपने स्कूल से पूछें, या हमें {email} पर लिखें। हम गोपनीयता से जुड़े हर अनुरोध का जवाब 30 दिनों के भीतर देते हैं।",
        cta: "गोपनीयता के लिए संपर्क करें",
      },
      sections: [
        {
          title: "हम कौन हैं",
          tag: "परिचय",
          paragraphs: [
            "Kaympus एक स्कूल प्रबंधन प्लेटफ़ॉर्म है, जिसे Kayease Global Solutions LLP, जयपुर, राजस्थान, भारत (“Kaympus”, “हम”) चलाता है। यह नीति Kaympus वेबसाइट (kaympus.com), हर स्कूल के Kaympus पोर्टल और स्कूल वेबसाइट, तथा Android और iOS के लिए Kaympus मोबाइल ऐप पर लागू होती है।",
            "स्कूल अपने रोज़ के काम के लिए Kaympus का उपयोग करते हैं। Kaympus में ज़्यादातर जानकारी — छात्र रिकॉर्ड, उपस्थिति, फ़ीस आदि — आपका स्कूल दर्ज करता है और वही उसे नियंत्रित करता है। ऐसी जानकारी के लिए स्कूल तय करता है कि क्या लिया जाए और क्यों, और हम उसे स्कूल की ओर से और उसके निर्देश पर संभालते हैं। भारत के डिजिटल व्यक्तिगत डेटा संरक्षण अधिनियम, 2023 के तहत स्कूल डेटा फ़िड्युशियरी है और हम उसके डेटा प्रोसेसर हैं। जो जानकारी हम अपने लिए लेते हैं, जैसे हमारी वेबसाइट पर डेमो अनुरोध या स्कूल की सदस्यता का भुगतान, उसकी ज़िम्मेदारी हमारी है।",
          ],
          bullets: [],
          after: ["अगर कुछ भी स्पष्ट न हो, तो हमें {email} पर संपर्क करें।"],
        },
        {
          title: "हम कौन-सी जानकारी लेते हैं",
          tag: "हम क्या लेते हैं",
          paragraphs: [
            "हम केवल वही जानकारी लेते हैं जो आप जिस सुविधा का उपयोग कर रहे हैं उसके लिए ज़रूरी है। आपकी भूमिका (छात्र, अभिभावक, शिक्षक, स्टाफ़ या स्कूल प्रशासक) और स्कूल द्वारा चालू की गई सुविधाओं के अनुसार इसमें ये शामिल हो सकते हैं:",
          ],
          bullets: [
            { term: "खाते का विवरण", desc: "नाम, मोबाइल नंबर, ईमेल पता, भूमिका, स्कूल कोड और कर्मचारी या प्रवेश संख्या। आप अपने मोबाइल नंबर और SMS से भेजे गए वन-टाइम पासवर्ड (OTP) से साइन इन करते हैं।" },
            { term: "छात्र रिकॉर्ड", desc: "स्कूल द्वारा दर्ज: नाम, फ़ोटो, जन्मतिथि, लिंग, कक्षा और सेक्शन, माता-पिता या अभिभावकों के नाम और फ़ोन नंबर, तथा पता। केवल तभी जब स्कूल दर्ज करे: आधार नंबर, धर्म, श्रेणी, ब्लड ग्रुप, चिकित्सा नोट्स और प्रमाणपत्र।" },
            { term: "स्टाफ़ रिकॉर्ड", desc: "स्कूल द्वारा दर्ज: नाम, फ़ोटो, पद, विभाग, संपर्क विवरण, दस्तावेज़, उपस्थिति, छुट्टी और वेतन की जानकारी।" },
            { term: "स्कूल गतिविधि", desc: "उपस्थिति, समय-सारणी, होमवर्क और आपके जमा किए गए असाइनमेंट, परीक्षा के अंक और रिपोर्ट कार्ड, सूचनाएँ, संदेश, आपके हस्ताक्षर किए सहमति फ़ॉर्म (बनाए गए हस्ताक्षर सहित), और लाइब्रेरी, हॉस्टल, खेल और परिवहन रिकॉर्ड।" },
            { term: "फ़ीस और भुगतान", desc: "फ़ीस की राशि, देय तिथियाँ, रसीदें और भुगतान संदर्भ। कार्ड, UPI और बैंक विवरण भुगतान प्रदाता की अपनी सुरक्षित स्क्रीन पर दर्ज होते हैं। Kaympus उन्हें कभी नहीं देखता या रखता।" },
            { term: "चेहरा और फ़िंगरप्रिंट डेटा (केवल स्टाफ़)", desc: "चेहरा पहचान उपस्थिति के लिए फ़ोटो और फ़ेस टेम्पलेट, और जहाँ स्कूल फ़िंगरप्रिंट स्कैनर उपयोग करता है वहाँ फ़िंगरप्रिंट टेम्पलेट। नीचे “चेहरा पहचान” देखें।" },
            { term: "लोकेशन", desc: "स्टाफ़ के उपस्थिति दर्ज करते समय आपके फ़ोन की लोकेशन, और ड्राइवर के परिवहन ट्रिप चलाते समय बस की लोकेशन। नीचे “लोकेशन डेटा” देखें।" },
            { term: "Wi-Fi एक्सेस पॉइंट पहचानकर्ता", desc: "स्टाफ़ के उपस्थिति दर्ज करते समय ऐप उस Wi-Fi एक्सेस पॉइंट का पहचानकर्ता (BSSID) पढ़ता है जिससे फ़ोन जुड़ा है, ताकि स्कूल पुष्टि कर सके कि स्टाफ़ परिसर में है। हम नेटवर्क का नाम या उस पर भेजी गई कोई भी चीज़ नहीं पढ़ते।" },
            { term: "डिवाइस और ऐप की जानकारी", desc: "डिवाइस मॉडल, ऑपरेटिंग सिस्टम संस्करण और ऐप संस्करण, जो उपस्थिति पंच और समस्या रिपोर्ट के साथ भेजे जाते हैं। आपके Kaympus उपयोग करते समय हमारे सर्वर आपका IP पता दर्ज करते हैं। हम आपकी विज्ञापन ID, IMEI, फ़ोन सीरियल नंबर या संपर्क नहीं लेते।" },
            { term: "आपके अपलोड किए फ़ोटो और फ़ाइलें", desc: "दस्तावेज़, असाइनमेंट PDF, पिकअप अभिभावकों के फ़ोटो, और सहायता अनुरोधों के साथ लगाए गए स्क्रीनशॉट।" },
            { term: "स्कैन किए गए कार्ड और कोड", desc: "जब स्कूल स्टाफ़ किसी छात्र का परिवहन कार्ड, गेट पास या लाइब्रेरी की किताब स्कैन करता है, तो हम दर्ज करते हैं कि क्या स्कैन हुआ, किसने और कब।" },
            { term: "सहायता अनुरोध", desc: "Help & Support से भेजे गए संदेश और स्क्रीनशॉट, साथ में बुनियादी डिवाइस जानकारी जो समस्या ठीक करने में मदद करती है।" },
            { term: "वेबसाइट आगंतुक", desc: "अगर आप डेमो माँगते हैं या हमसे संपर्क करते हैं: आपके दर्ज किए विवरण (नाम, स्कूल, फ़ोन, ईमेल, संदेश)। हमारी मार्केटिंग वेबसाइट Google Analytics भी उपयोग करती है, जिसका वर्णन नीचे “कुकीज़” में है।" },
          ],
          after: [],
        },
        {
          title: "चेहरा पहचान",
          tag: "बायोमेट्रिक",
          paragraphs: [
            "चेहरा पहचान उपस्थिति केवल स्कूल स्टाफ़ के लिए है, और केवल तभी जब स्कूल इसे चालू करे। यह कभी भी छात्रों या अभिभावकों के लिए उपयोग नहीं होती।",
          ],
          bullets: [
            { term: "नामांकन", desc: "स्टाफ़ सदस्य ऐप में चार फ़ोटो लेता है। फ़ोटो हमारे सर्वर पर भेजे जाते हैं, जहाँ एक फ़ेस टेम्पलेट (चेहरे का वर्णन करने वाली संख्याओं का समूह) बनाया जाता है। उपयोग से पहले स्कूल प्रशासक को नामांकन स्वीकृत करना होता है। फ़ोटो और टेम्पलेट स्टाफ़ सदस्य के रिकॉर्ड के साथ रखे जाते हैं।" },
            { term: "उपस्थिति दर्ज करना", desc: "ऐप पहले फ़ोन पर ही जाँचता है कि कैमरे के सामने असली व्यक्ति है, जैसे पलक झपकाने या सिर घुमाने को कहकर। यह जाँच फ़ोन पर होती है और वे कैमरा फ़्रेम अपलोड नहीं होते। फिर एक फ़ोटो आपके फ़ेस टेम्पलेट से मिलान के लिए हमारे सर्वर पर भेजी जाती है, और पंच के प्रमाण के रूप में उस उपस्थिति रिकॉर्ड के साथ रखी जाती है।" },
            { term: "गेट कियोस्क", desc: "कुछ स्कूल गेट पर एक साझा फ़ोन या टैबलेट लगाते हैं। इंटरनेट के बिना भी काम करते रहने के लिए वह डिवाइस स्कूल के नामांकित स्टाफ़ के फ़ेस टेम्पलेट रखता है, और पंच फ़ोटो तब तक रखता है जब तक उन्हें अपलोड न कर सके।" },
            { term: "फ़िंगरप्रिंट", desc: "जहाँ स्कूल डेस्क फ़िंगरप्रिंट स्कैनर उपयोग करता है, वहाँ फ़िंगरप्रिंट टेम्पलेट स्टाफ़ सदस्य के रिकॉर्ड के साथ रखे जाते हैं और इसी तरह उपयोग होते हैं।" },
          ],
          after: [
            "हम चेहरा और फ़िंगरप्रिंट डेटा केवल उपस्थिति दर्ज करने के लिए उपयोग करते हैं। हम इसे कभी नहीं बेचते, विज्ञापन के लिए उपयोग नहीं करते, और आपके स्कूल के अलावा किसी के साथ साझा नहीं करते। अपना चेहरा या फ़िंगरप्रिंट डेटा हटवाने के लिए अपने स्कूल प्रशासक से कहें या हमें {email} पर लिखें।",
          ],
        },
        {
          title: "लोकेशन डेटा",
          tag: "लोकेशन",
          paragraphs: ["हम आपके फ़ोन की लोकेशन केवल तीन स्थितियों में उपयोग करते हैं:"],
          bullets: [
            { term: "स्टाफ़ उपस्थिति", desc: "जब स्टाफ़ सदस्य उपस्थिति दर्ज करने के लिए टैप करता है, तो ऐप यह जाँचने के लिए एक बार लोकेशन पढ़ता है कि वह स्कूल परिसर में है। लोकेशन और उसकी सटीकता उस उपस्थिति रिकॉर्ड के साथ सहेजी जाती है। अगर स्कूल ने परिसर जाँच चालू नहीं की है, तो लोकेशन की ज़रूरत नहीं होती।" },
            { term: "बस ट्रिप (ड्राइवर और अटेंडेंट)", desc: "जब ड्राइवर या अटेंडेंट की बस ट्रिप चल रही होती है, तो ऐप लगभग हर 15 सेकंड में बस की लोकेशन साझा करता है ताकि अभिभावक और स्कूल देख सकें कि बस कहाँ है। Android पर यह स्क्रीन बंद होने पर भी चलता रहता है और एक स्थायी “बस ट्रिप जारी है” सूचना दिखाता है। ट्रिप खत्म होने या ऐप बंद होने पर ट्रैकिंग रुक जाती है। हम ट्रिप के बाहर किसी ड्राइवर को कभी ट्रैक नहीं करते।" },
            { term: "बस स्टॉप लगाना", desc: "परिवहन स्टाफ़ नक्शे पर बस स्टॉप लगाने के लिए एक बार “मेरी लोकेशन उपयोग करें” टैप कर सकता है।" },
          ],
          after: [
            "हम छात्र या अभिभावक खातों से लोकेशन नहीं लेते। अभिभावक बस की स्थिति देखते हैं, अपनी नहीं। बस की लोकेशन का इतिहास अधिकतम 90 दिनों में, या स्कूल चाहे तो उससे पहले, अपने आप मिट जाता है।",
          ],
        },
        {
          title: "ऐप अनुमतियाँ और हम क्यों माँगते हैं",
          tag: "अनुमतियाँ",
          paragraphs: [
            "ऐप कोई अनुमति तभी माँगता है जब आप उसकी ज़रूरत वाली सुविधा उपयोग करते हैं। आप फ़ोन की सेटिंग्स में कोई भी अनुमति मना कर सकते हैं या वापस ले सकते हैं; केवल उससे जुड़ी सुविधा काम करना बंद करती है।",
          ],
          bullets: [
            { term: "कैमरा", desc: "चेहरा पहचान उपस्थिति, QR कोड और बारकोड स्कैन करना (परिवहन, गेट पास, लाइब्रेरी की किताबें, कियोस्क सेटअप), और पिकअप कार्ड के लिए अभिभावकों के फ़ोटो लेना।" },
            { term: "लोकेशन (सटीक)", desc: "उपस्थिति दर्ज करते समय स्टाफ़ के परिसर में होने की जाँच, और परिवहन ट्रिप के दौरान बस की लोकेशन साझा करना।" },
            { term: "फ़ोरग्राउंड सेवा में लोकेशन", desc: "केवल ड्राइवर और अटेंडेंट द्वारा बस ट्रिप चलने के दौरान, पूरी ट्रिप में दिखने वाली सूचना के साथ।" },
            { term: "आस-पास के डिवाइस (Bluetooth)", desc: "बस पर स्कूल के RFID कार्ड रीडर से जुड़ना। ऐप केवल उसी रीडर को खोजता है और आपकी लोकेशन या अन्य डिवाइस खोजने के लिए Bluetooth उपयोग नहीं करता।" },
            { term: "Wi-Fi स्थिति", desc: "स्टाफ़ उपस्थिति के दौरान Wi-Fi एक्सेस पॉइंट पहचानकर्ता पढ़ना, और यह जाँचना कि आप ऑनलाइन हैं या नहीं।" },
            { term: "सूचनाएँ", desc: "आपके स्कूल के संदेश, सूचनाएँ, फ़ीस रिमाइंडर और अलर्ट दिखाना।" },
            { term: "फ़ाइलें और फ़ोटो", desc: "केवल वे फ़ाइलें जिन्हें आप सिस्टम फ़ाइल पिकर से लगाने के लिए चुनते हैं। ऐप आपकी स्टोरेज स्कैन नहीं करता।" },
          ],
          after: ["ऐप आपके संपर्क, कॉल लॉग, SMS संदेश या माइक्रोफ़ोन तक नहीं पहुँचता।"],
        },
        {
          title: "हम जानकारी का उपयोग कैसे करते हैं",
          tag: "उद्देश्य",
          paragraphs: ["हम जानकारी का उपयोग इनके लिए करते हैं:"],
          bullets: [
            { term: "सेवा चलाना", desc: "आपको साइन इन कराना, आपके स्कूल की जानकारी दिखाना, और आपके स्कूल द्वारा उपयोग की जाने वाली सुविधाएँ देना।" },
            { term: "संवाद", desc: "आपके स्कूल के लिए OTP, सूचनाएँ, SMS और ईमेल भेजना, जैसे उपस्थिति अलर्ट और फ़ीस रिमाइंडर।" },
            { term: "भुगतान प्रक्रिया", desc: "आपके चुने हुए भुगतान प्रदाता के माध्यम से फ़ीस और सदस्यता भुगतान बनाना और उनकी पुष्टि करना।" },
            { term: "Kaympus को सुरक्षित रखना", desc: "दुरुपयोग पकड़ना, समस्याओं की जाँच करना, और हर स्कूल में किए गए बदलावों का गतिविधि लॉग रखना।" },
            { term: "आपकी सहायता", desc: "आपके सवालों का जवाब देना और आपकी बताई समस्याएँ ठीक करना।" },
            { term: "कानूनी कर्तव्य", desc: "कानून, अदालती आदेशों और अधिकारियों के वैध अनुरोधों का पालन करना।" },
          ],
          after: [
            "हम व्यक्तिगत डेटा नहीं बेचते। ऐप में कोई विज्ञापन नहीं है, और हम आपका या आपके बच्चे का डेटा कभी विज्ञापन या मार्केटिंग प्रोफ़ाइल बनाने के लिए उपयोग नहीं करते।",
          ],
        },
        {
          title: "हम किसके साथ साझा करते हैं",
          tag: "साझा करना",
          paragraphs: ["हम जानकारी केवल यहाँ बताए अनुसार साझा करते हैं:"],
          bullets: [
            { term: "आपका स्कूल", desc: "आपके स्कूल के अधिकृत लोग अपनी भूमिका के अनुसार जानकारी देखते हैं। जैसे, कक्षा शिक्षक अपनी कक्षा देखता है। अभिभावक केवल अपने बच्चे की जानकारी देखते हैं।" },
            { term: "SMS भेजना", desc: "SMSGatewayHub, OTP और SMS संदेश भेजने के लिए (आपका फ़ोन नंबर और संदेश का पाठ)।" },
            { term: "ईमेल भेजना", desc: "Google Workspace (Gmail), ईमेल भेजने के लिए।" },
            { term: "ऐप सूचनाएँ", desc: "Google Firebase Cloud Messaging, सूचनाएँ पहुँचाने के लिए (एक डिवाइस टोकन और सूचना का पाठ)।" },
            { term: "भुगतान", desc: "Razorpay, PhonePe, Paytm, PayU या Cashfree, जो भी आपका स्कूल उपयोग करता है। उन्हें भुगतानकर्ता का नाम, फ़ोन, ईमेल और राशि मिलती है, और वे कार्ड और UPI विवरण अपनी गोपनीयता नीतियों के तहत स्वयं संभालते हैं।" },
            { term: "नक्शे", desc: "OpenStreetMap, या स्कूल द्वारा चुना गया कोई अन्य नक्शा प्रदाता, परिवहन नक्शों के लिए नक्शा चित्र देता है। आपका डिवाइस उन्हें सीधे माँगता है, इसलिए प्रदाता आपका IP पता देखता है पर कोई Kaympus डेटा नहीं।" },
            { term: "फ़ॉन्ट", desc: "ऐप अपने फ़ॉन्ट Google Fonts से डाउनलोड करता है, जो आपका IP पता देखता है।" },
            { term: "होस्टिंग", desc: "हमारे सर्वर और फ़ाइल स्टोरेज किराए के क्लाउड सर्वरों पर चलते हैं, जो हमारे लिए डेटा रखते हैं।" },
            { term: "कानून और सुरक्षा", desc: "अधिकारी, जब कानून इसकी माँग करे या किसी की सुरक्षा के लिए।" },
            { term: "व्यावसायिक बदलाव", desc: "अगर हमारे व्यवसाय का विलय या बिक्री होती है तो खरीदार या उत्तराधिकारी, जिसे इस नीति के तहत डेटा की रक्षा करते रहना होगा।" },
          ],
          after: ["हम हर सेवा प्रदाता को केवल उतनी ही जानकारी देते हैं जितनी उसके काम के लिए ज़रूरी है।"],
        },
        {
          title: "बच्चों का डेटा",
          tag: "बच्चे",
          paragraphs: [
            "Kaympus के कई उपयोगकर्ता 18 वर्ष से कम उम्र के छात्र हैं। छात्र खाते स्कूल बनाता है, बच्चा नहीं, और अभिभावक आमतौर पर अपने मोबाइल नंबर से अपने बच्चे के खाते में साइन इन करते हैं।",
            "डिजिटल व्यक्तिगत डेटा संरक्षण अधिनियम, 2023 के अनुसार, किसी बच्चे की जानकारी Kaympus में जोड़ने से पहले माता-पिता या अभिभावक की सहमति लेना स्कूल की ज़िम्मेदारी है। हम बच्चों का डेटा केवल स्कूल की सेवाएँ देने के लिए उपयोग करते हैं। हम बच्चों को विज्ञापन नहीं दिखाते, विज्ञापन के लिए उन्हें ट्रैक नहीं करते, और स्कूल के अपने रिकॉर्ड (जैसे उपस्थिति) से आगे उनके व्यवहार की निगरानी नहीं करते।",
          ],
          bullets: [],
          after: ["जो अभिभावक अपने बच्चे का डेटा देखना, सुधारना या मिटवाना चाहते हैं, वे स्कूल से कह सकते हैं या हमें {email} पर लिख सकते हैं।"],
        },
        {
          title: "हम इसे कितने समय तक रखते हैं",
          tag: "अवधि",
          paragraphs: ["हम जानकारी केवल तब तक रखते हैं जब तक उसकी ज़रूरत है:"],
          bullets: [
            { term: "स्कूल रिकॉर्ड", desc: "जब तक स्कूल Kaympus उपयोग करता है तब तक रखे जाते हैं, जब तक स्कूल उन्हें पहले न मिटा दे। छात्र और स्टाफ़ रिकॉर्ड कितने समय रखने हैं यह स्कूल तय करता है, और कुछ रिकॉर्ड (जैसे फ़ीस रसीदें और स्कूल छोड़ने के प्रमाणपत्र) उसे कानूनन रखने पड़ सकते हैं।" },
            { term: "जब कोई स्कूल छोड़ता है", desc: "अगर किसी स्कूल का खाता मिटाया जाता है, तो उसे 30 दिनों तक वापस लाया जा सकता है। उसके बाद उसके रिकॉर्ड हमारे डेटाबेस से स्थायी रूप से मिटा दिए जाते हैं। स्कूल उससे पहले अपने डेटा की पूरी प्रति डाउनलोड कर सकता है। फ़ोटो और दस्तावेज़ जैसी अपलोड की गई फ़ाइलें स्कूल या उपयोगकर्ता के कहने पर मिटाई जाती हैं।" },
            { term: "बस की लोकेशन का इतिहास", desc: "अधिकतम 90 दिनों में अपने आप मिट जाता है।" },
            { term: "वन-टाइम पासवर्ड", desc: "कुछ ही मिनटों में समाप्त हो जाते हैं और उपयोग के बाद मिटा दिए जाते हैं।" },
            { term: "गतिविधि लॉग", desc: "365 दिनों के बाद अपने आप मिट जाते हैं।" },
            { term: "सर्वर लॉग", desc: "तकनीकी लॉग, जिनमें IP पते होते हैं, लगभग 14 दिनों में बदलकर मिटा दिए जाते हैं।" },
            { term: "सूचनाएँ", desc: "ऐप की सूचनाएँ आपके पढ़ने के 90 दिन बाद मिटा दी जाती हैं।" },
            { term: "डेमो और संपर्क अनुरोध", desc: "जब तक हम आपके संपर्क में हैं तब तक रखे जाते हैं। आप कभी भी इन्हें मिटाने को कह सकते हैं।" },
          ],
          after: [],
        },
        {
          title: "हम इसकी सुरक्षा कैसे करते हैं",
          tag: "सुरक्षा",
          paragraphs: ["हम आपकी जानकारी की सुरक्षा के लिए उचित तकनीकी और संगठनात्मक कदम उठाते हैं, जिनमें शामिल हैं:"],
          bullets: [
            { term: "एन्क्रिप्टेड कनेक्शन", desc: "ऐप या ब्राउज़र और हमारे सर्वरों के बीच सारा ट्रैफ़िक HTTPS (TLS) का उपयोग करता है।" },
            { term: "अलग स्कूल डेटाबेस", desc: "हर स्कूल का डेटा उसके अपने डेटाबेस में रखा जाता है, और हर अनुरोध की उपयोगकर्ता के स्कूल और भूमिका के अनुसार जाँच होती है।" },
            { term: "सुरक्षित साइन-इन", desc: "सीमित प्रयासों और कम समय-सीमा वाला OTP साइन-इन। आपका साइन-इन टोकन आपके फ़ोन के सुरक्षित स्टोरेज (Android Keystore या iOS Keychain) में रखा जाता है।" },
            { term: "सुरक्षित क्रेडेंशियल", desc: "भुगतान गेटवे की कुंजियाँ और अन्य सेवा क्रेडेंशियल एन्क्रिप्ट करके रखे जाते हैं।" },
            { term: "गतिविधि लॉगिंग", desc: "हर स्कूल में किए गए बदलाव एक गतिविधि लॉग में दर्ज होते हैं जिसे स्कूल देख सकता है।" },
          ],
          after: [
            "कोई भी सिस्टम पूरी तरह सुरक्षित नहीं होता। अगर किसी उल्लंघन से आपका व्यक्तिगत डेटा प्रभावित होता है, तो हम प्रभावित स्कूल को, और जहाँ कानून माँगे वहाँ भारत के डेटा संरक्षण बोर्ड को, सूचित करेंगे।",
          ],
        },
        {
          title: "आपके विकल्प और अधिकार",
          tag: "आपके अधिकार",
          paragraphs: ["डिजिटल व्यक्तिगत डेटा संरक्षण अधिनियम, 2023 के तहत आपको ये अधिकार हैं:"],
          bullets: [
            { term: "पहुँच", desc: "आपके बारे में रखे गए व्यक्तिगत डेटा और उसके उपयोग का सारांश पाना।" },
            { term: "सुधार", desc: "गलत या अधूरा डेटा ठीक करवाना। आप ऐप में अपना ईमेल और आपातकालीन संपर्क बदल सकते हैं; बाकी विवरण आपका स्कूल अपडेट करता है।" },
            { term: "मिटाना", desc: "ज़रूरत न रहने पर अपना डेटा मिटवाने को कहना, सिवाय उन रिकॉर्ड के जो आपके स्कूल को कानूनन रखने हैं।" },
            { term: "सहमति वापस लेना", desc: "दी गई सहमति वापस लेना, जैसे कोई अनुमति बंद करके या अपना चेहरा डेटा हटवाने को कहकर।" },
            { term: "शिकायत", desc: "हमसे शिकायत करना और जवाब पाना। संतुष्ट न होने पर आप भारत के डेटा संरक्षण बोर्ड से शिकायत कर सकते हैं।" },
            { term: "नामांकित करना", desc: "मृत्यु या अक्षमता की स्थिति में अपनी ओर से इन अधिकारों का उपयोग करने के लिए किसी को नामित करना।" },
          ],
          after: [
            "क्योंकि आपका ज़्यादातर डेटा आपका स्कूल नियंत्रित करता है, इसलिए स्कूल से पूछना आमतौर पर सबसे जल्दी होता है। आप हमें {email} पर भी लिख सकते हैं। हम आपकी पहचान की जाँच करेंगे, ज़रूरत हो तो आपके स्कूल को शामिल करेंगे, और 30 दिनों के भीतर जवाब देंगे।",
          ],
        },
        {
          title: "अपना खाता और डेटा मिटाना",
          tag: "मिटाना",
          paragraphs: [
            "Kaympus खाते आपका स्कूल बनाता है, इसलिए आपका स्कूल कभी भी आपका खाता निष्क्रिय कर सकता है या मिटा सकता है। आप सीधे हमसे भी कह सकते हैं:",
          ],
          bullets: [
            { term: "चरण 1", desc: "{email} पर “Delete my Kaympus account” विषय के साथ ईमेल करें।" },
            { term: "चरण 2", desc: "अपने स्कूल का नाम या स्कूल कोड, अपना पंजीकृत मोबाइल नंबर, और यह लिखें कि आप छात्र/अभिभावक, शिक्षक या स्टाफ़ सदस्य हैं।" },
            { term: "चरण 3", desc: "हम आपसे अनुरोध की पुष्टि करते हैं और आपके स्कूल को बताते हैं, 30 दिनों के भीतर आपका खाता मिटाते हैं, और पूरा होने पर आपको ईमेल करते हैं।" },
          ],
          after: [
            "क्या मिटाया जाता है: आपका साइन-इन खाता, प्रोफ़ाइल और संपर्क विवरण, सूचना टोकन, आपके अपलोड किए फ़ोटो और दस्तावेज़, और स्टाफ़ के लिए चेहरे के फ़ोटो, फ़ेस टेम्पलेट और फ़िंगरप्रिंट टेम्पलेट।",
            "क्या रखा जा सकता है: वे रिकॉर्ड जो आपके स्कूल को कानूनन रखने हैं, जैसे फ़ीस रसीदें, उपस्थिति रजिस्टर और स्कूल छोड़ने के प्रमाणपत्र। ये केवल उतने समय तक रखे जाते हैं जितना कानून माँगता है और किसी सक्रिय खाते से जुड़े नहीं रहते।",
          ],
        },
        {
          title: "कुकीज़ और ब्राउज़र स्टोरेज",
          tag: "कुकीज़",
          paragraphs: [
            "जब आप वेब ब्राउज़र में किसी Kaympus पोर्टल में साइन इन करते हैं, तो हम एक साइन-इन कुकी रखते हैं ताकि आप साइन इन रहें, और आपके ब्राउज़र के लोकल स्टोरेज में ऐसी चीज़ें याद रखते हैं जैसे आप किन खातों के बीच बदलते हैं। ये पोर्टल के काम करने के लिए ज़रूरी हैं।",
            "हमारी मार्केटिंग वेबसाइट (kaympus.com, स्कूल पोर्टल या ऐप नहीं) विज़िट गिनने और यह देखने के लिए कि कौन-से पेज उपयोगी हैं, Google Tag Manager और Google Analytics उपयोग करती है। इसके लिए Google अपनी कुकीज़ लगाता है। आप इन्हें अपनी ब्राउज़र सेटिंग्स में या Google के Analytics opt-out add-on से रोक सकते हैं।",
            "मोबाइल ऐप कुकीज़ उपयोग नहीं करता, और उसमें कोई एनालिटिक्स या विज्ञापन SDK नहीं है।",
          ],
          bullets: [],
          after: [],
        },
        {
          title: "बदलाव और संपर्क",
          tag: "अद्यतन",
          paragraphs: [
            "Kaympus में बदलाव के साथ हम यह नीति अपडेट कर सकते हैं। हम इस पेज के ऊपर की तारीख बदलेंगे, और महत्वपूर्ण बदलावों के बारे में स्कूलों को बताएँगे।",
            "Kayease Global Solutions LLP, जयपुर, राजस्थान, भारत। गोपनीयता और शिकायत संपर्क: {email}, फ़ोन {phone}।",
          ],
          bullets: [],
          after: [],
        },
      ],
    },
    terms: {
      heading: "हमारी <em>साझेदारी की</em>\nबुनियाद।",
      subtitle:
        "स्पष्ट, पारदर्शी और निष्पक्ष क़ानूनी दिशा-निर्देश, जो आपके संस्थान की डिजिटल अखंडता और हमारी सेवा प्रतिबद्धता — दोनों की रक्षा करते हैं।",
      sections: [
        {
          title: "शर्तों की स्वीकृति",
          tag: "क़ानूनी",
          content: "Kaympus का उपयोग करते ही आप इन नियमों और शर्तों से बँधने पर सहमत होते हैं। ये शर्तें आपके संस्थान और Kaympus के बीच, हमारे स्कूल ERP प्लेटफ़ॉर्म और संबंधित सेवाओं के उपयोग को लेकर, एक क़ानूनी रूप से बाध्यकारी अनुबंध हैं।",
          details: ["बाध्यकारी अनुबंध", "संस्थागत उत्तरदायित्व", "सेवा स्वीकृति", "क़ानूनी अनुपालन"],
        },
        {
          title: "सेवा का प्रावधान",
          tag: "प्लेटफ़ॉर्म",
          content: "हम शैक्षणिक संस्थानों के लिए बना एक मॉड्यूलर ERP प्लेटफ़ॉर्म देते हैं। हम 99.9% अपटाइम का प्रयास करते हैं, फिर भी सेवाएँ जिस रूप में उपलब्ध हैं उसी रूप में दी जाती हैं। सक्रिय संस्थागत प्रशासकों को पूर्व सूचना देकर सेवा के किसी भी हिस्से में बदलाव करने, उसे रोकने या बंद करने का अधिकार हमारे पास सुरक्षित है।",
          details: ["अपटाइम प्रतिबद्धता", "फ़ीचर अपडेट", "निर्धारित रखरखाव", "सेवा उपलब्धता"],
        },
        {
          title: "स्वीकार्य उपयोग",
          tag: "आचरण",
          content: "उपयोगकर्ता इस प्लेटफ़ॉर्म का उपयोग केवल वैध शैक्षणिक और प्रशासनिक कार्यों के लिए करें। रिवर्स इंजीनियरिंग, डेटा स्क्रैपिंग या सुरक्षा प्रोटोकॉल को दरकिनार करने का कोई भी प्रयास सख़्त मना है और इससे सेवा तुरंत बंद की जा सकती है तथा क़ानूनी कार्रवाई हो सकती है।",
          details: ["निषिद्ध गतिविधियाँ", "सुरक्षा अखंडता", "अधिकृत पहुँच", "डेटा का नैतिक उपयोग"],
        },
        {
          title: "दायित्व की सीमा",
          tag: "प्रशासन",
          content: "हमारे प्लेटफ़ॉर्म के उपयोग से होने वाली किसी भी अप्रत्यक्ष, आनुषंगिक या परिणामी हानि के लिए Kaympus उत्तरदायी नहीं होगा। अपने डेटा का बैकअप रखना और सिस्टम में दर्ज जानकारी की शुद्धता सुनिश्चित करना संस्थानों की ज़िम्मेदारी है।",
          details: ["दायित्व की सीमा", "डेटा की शुद्धता", "उपयोगकर्ता उत्तरदायित्व", "क्षतिपूर्ति"],
        },
      ],
    },
    refund: {
      heading: "हमारी रद्दीकरण और\n<em>बिना-धनवापसी नीति।</em>",
      subtitle:
        "Kaympus सदस्यता का हर भुगतान अंतिम है। यहाँ स्पष्ट रूप से बताया गया है कि रद्दीकरण कैसे काम करता है, शुल्क वापसी योग्य क्यों नहीं हैं, और भुगतान में कभी गड़बड़ी हो तो क्या होता है।",
      sections: [
        {
          title: "सदस्यता रद्द करना",
          tag: "रद्दीकरण",
          content: "आपका संस्थान अपनी Kaympus सदस्यता कभी भी सदस्यता सेटिंग्स से या हमारी सपोर्ट टीम को लिखकर रद्द कर सकता है। रद्द करने से अगला नवीनीकरण रुक जाता है — जिस अवधि का भुगतान हो चुका है उसके अंत तक आपके स्कूल की प्लेटफ़ॉर्म और डेटा तक पूरी पहुँच बनी रहती है। उस अवधि के लिए दी जा चुकी राशि वापस नहीं की जाती, और रद्दीकरण का कोई शुल्क नहीं लगता।",
          details: ["कभी भी रद्द करें", "अवधि के अंत तक पहुँच", "नवीनीकरण तुरंत रुकता है", "कोई रद्दीकरण शुल्क नहीं"],
        },
        {
          title: "सदस्यता पर कोई धनवापसी नहीं",
          tag: "नीति",
          content: "सभी सदस्यता शुल्क अंतिम हैं। Kaympus सदस्यता ख़रीदने और सक्रिय होने के बाद भुगतान किसी भी परिस्थिति में वापसी योग्य नहीं है — चाहे बिलिंग अवधि का समय बचा हो, प्लेटफ़ॉर्म का उपयोग कम या बिल्कुल न हुआ हो, प्लान डाउनग्रेड किया गया हो, या इरादा बदल गया हो। हम हर स्कूल से आग्रह करते हैं कि ख़रीदने से पहले फ्री डेमो लेकर Kaympus को पूरी तरह परख लें।",
          details: ["सभी भुगतान अंतिम", "कोई आनुपातिक वापसी नहीं", "डाउनग्रेड पर वापसी नहीं", "ख़रीद से पहले फ्री डेमो"],
        },
        {
          title: "भुगतान की गड़बड़ियाँ",
          tag: "अपवाद",
          content: "हम केवल वास्तविक लेन-देन त्रुटियों की राशि लौटाते हैं: एक ही ऑर्डर पर दो बार कटा भुगतान, या खाते से राशि कटने के बावजूद सदस्यता सक्रिय न होना। ये असफल भुगतान का सुधार हैं — ख़रीदी गई सदस्यता की धनवापसी नहीं — और यह राशि मूल भुगतान माध्यम में 7–10 कार्यदिवसों के भीतर लौटा दी जाती है।",
          details: ["दोहरा भुगतान लौटाया जाता है", "असफल कटौती की वापसी", "मूल भुगतान माध्यम", "7–10 कार्यदिवस"],
        },
        {
          title: "बिलिंग से जुड़ा प्रश्न उठाना",
          tag: "प्रक्रिया",
          content: "बिलिंग के किसी भी प्रश्न या लेन-देन त्रुटि की सूचना के लिए अपनी स्कूल कोड और भुगतान संदर्भ के साथ हमारी सपोर्ट टीम को लिखें, या संपर्क पेज से हम तक पहुँचें। हर प्रश्न की समीक्षा 2–3 कार्यदिवसों में होती है और समाधान होने तक हम आपको हर चरण पर सूचित रखते हैं।",
          details: ["ईमेल या संपर्क पेज", "स्कूल कोड + भुगतान संदर्भ", "2–3 दिन में समीक्षा", "समाधान तक अपडेट"],
        },
      ],
    },
  },

  blog: {
    heading: "Kaympus <em>ब्लॉग</em>",
    subtitle: "आधुनिक स्कूल चलाने के लिए गाइड, प्रोडक्ट अपडेट और विचार।",
    categoryTabs: ["सभी लेख", "स्कूल संचालन", "फीस प्रबंधन", "ख़रीद गाइड"],
    filterNotice: "इस श्रेणी के गाइड और लेख:",
    clearFilter: "फ़िल्टर हटाएँ (सभी देखें) →",
    emptyTitle: "इस श्रेणी में कोई पोस्ट नहीं मिली।",
    emptyBody: "दूसरी श्रेणियाँ देखिए या सभी लेख देखिए।",
    viewAll: "सभी लेख देखें",
  },

  support: {
    badge: "Kaympus सपोर्ट और हेल्पडेस्क",
    heading: "हम आपकी <em>कैसे मदद</em> करें?",
    subtitle: "सेटअप, फीस वसूली, अभिभावकों को सूचना, परीक्षा और परिवहन से जुड़े जवाब खोजिए, या नीचे दी गई श्रेणियाँ देखिए।",
    searchPlaceholder: "लेख, गाइड, फीस सेटअप खोजिए...",
    searchLabel: "Kaympus सपोर्ट गाइड और जवाब खोजें",
    clearSearch: "खोज हटाएँ",
    popular: ["फीस सेटअप", "SMS सूचनाएँ", "CBSE मार्कशीट", "GPS बस ट्रैकिंग", "छात्र आयात"],
    directLinks: ["सभी विशेषताएँ", "ब्लॉग और गाइड", "प्लान और कीमत", "सपोर्ट से संपर्क"],
    browseHeading: "<em>श्रेणी</em> के अनुसार देखिए",
    browseSubtitle: "जिस विषय में मदद चाहिए उसे चुनिए और हमारे ब्लॉग पर उसके गाइड और लेख पढ़िए।",
    exploreGuides: "गाइड देखें",
    resultsHeading: "इसके नतीजे:",
    resultsCountOne: "{count} संसाधन मिला",
    resultsCountOther: "{count} संसाधन मिले",
    allCategories: "सभी श्रेणियाँ",
    noResults: "“{query}” के लिए कोई नतीजा नहीं",
    noResultsBody: "कोई सामान्य शब्द आज़माइए, जैसे फीस, उपस्थिति या मार्कशीट — या सीधे हमसे पूछिए, हम जवाब देंगे।",
    srResultOne: "{query} के लिए {count} नतीजा",
    srResultOther: "{query} के लिए {count} नतीजे",
    faqHeading: "अक्सर पूछे जाने वाले <em>सवाल।</em>",
    faqLabel: "सामान्य सवाल",
    stillNeedHelp: "अब भी मदद चाहिए?",
    stillNeedHelpBody: "हमारी समर्पित स्कूल सक्सेस टीम 24/7 आपकी मदद के लिए तैयार है।",
    kinds: {
      answer: { label: "जवाब", cta: "और विस्तार से" },
      article: { label: "लेख", cta: "लेख पढ़ें" },
      page: { label: "Kaympus पर", cta: "पेज खोलें" },
    },
    topics: [
      { title: "शुरुआत और सेटअप", desc: "स्कूल की शुरुआती व्यवस्था, एडमिन प्रोफ़ाइल सेटअप, स्टाफ़ ऑनबोर्डिंग और बुनियादी सेटिंग।" },
      { title: "फीस वसूली और गेटवे", desc: "UPI/नेट बैंकिंग की सेटिंग, अपने हिसाब से फीस संरचना, स्वचालित रसीदें और बकाया सूचनाएँ।" },
      { title: "सूचनाएँ और अलर्ट", desc: "DLT SMS इंटीग्रेशन, अभिभावकों को स्वचालित सूचनाएँ, ब्रॉडकास्ट अलर्ट और ऐप के भीतर चैट।" },
      { title: "CBSE / ICSE रिपोर्ट कार्ड", desc: "परीक्षा योजना, ग्रेडिंग स्केल, कई सत्रों के परिणाम जोड़ना, और तुरंत PDF बनाना।" },
      { title: "परिवहन और लाइव GPS", desc: "रूट प्रबंधन, ड्राइवर आवंटन, RFID गेट चेक-इन और अभिभावकों के लिए बस ट्रैकिंग।" },
      { title: "सुरक्षा और अनुमतियाँ", desc: "भूमिका आधारित पहुँच, मल्टी-ब्रांच एडमिन अधिकार, डेटा एन्क्रिप्शन और ऑडिट लॉग।" },
    ],
    faqItems: [
      {
        question: "हमारा स्कूल Kaympus पर कितनी जल्दी शुरू हो सकता है?",
        answer: "Kaympus क्लाउड-आधारित है और आधुनिक स्कूलों के लिए पहले से तैयार आता है। हमारी टीम आपके छात्र, स्टाफ़ और फीस संरचना आयात करती है, कक्षाएँ और ग्रेडिंग सेट करती है, और ज़्यादातर स्कूल कुछ ही दिनों में लाइव हो जाते हैं।",
      },
      {
        question: "Kaympus अभिभावकों तक कैसे पहुँचता है?",
        answer: "सूचनाएँ DLT-पंजीकृत SMS गेटवे से, ईमेल आपके अपने SMTP खाते से, और पुश नोटिफ़िकेशन Kaympus मोबाइल ऐप से जाती हैं। शिक्षक और एडमिन ऐप के भीतर मौजूद चैट से अभिभावकों को सीधे संदेश भी भेज सकते हैं।",
      },
      {
        question: "क्या अभिभावक UPI या नेट बैंकिंग से ऑनलाइन फीस जमा कर सकते हैं?",
        answer: "बिल्कुल। अपना गेटवे जोड़िए (Razorpay, PayU, PhonePe, Paytm, Cashfree, Stripe या PayPal) और अभिभावक पोर्टल या मोबाइल ऐप से UPI, नेट बैंकिंग या कार्ड से भुगतान कर सकते हैं। हर भुगतान पर छात्र की फीस खाता-बही में क्रमांकित रसीद दर्ज होती है।",
      },
      {
        question: "क्या Kaympus CBSE ग्रेडिंग और मार्कशीट सँभालता है?",
        answer: "हाँ। रिपोर्ट कार्ड इंजन आपकी अपनी ग्रेडिंग व्यवस्था पर चलता है और CBSE जैसे सख़्त पास नियम भी सँभालता है, जहाँ थ्योरी में फेल होने पर पूरा विषय फेल माना जाता है। कार्ड पर आपके स्कूल का बोर्ड और संबद्धता संख्या के साथ लोगो, प्रधानाचार्य के हस्ताक्षर और मुहर छपती है।",
      },
      {
        question: "क्या अभिभावक स्कूल बस को लाइव देख सकते हैं?",
        answer: "हाँ। रूट को वाहन और ड्राइवर से जोड़ देने के बाद अभिभावक Kaympus ऐप में बस को लाइव नक़्शे पर चलते देख सकते हैं, और बस उनके स्टॉप के पास आते ही सूचना मिल जाती है। गेट चेक-इन छात्र की उपस्थिति में दर्ज हो जाता है।",
      },
      {
        question: "क्या हमारे स्कूल का डेटा सुरक्षित और नियमों के अनुरूप है?",
        answer: "हर स्कूल अपने अलग वर्कस्पेस में चलता है और सारा ट्रैफ़िक एन्क्रिप्टेड HTTPS से जाता है — साथ में विस्तृत रोल और अनुमति नियंत्रण, OTP से सत्यापित लॉगिन, निर्धारित बैकअप, और संवेदनशील गतिविधियों का ऐक्टिविटी लॉग जिसे आप कभी भी जाँच सकते हैं।",
      },
    ],
    resources: {
      "migration": {
        title: "Excel या पुराने ERP से छात्र और स्टाफ़ आयात करना",
        desc: "सहायता सहित माइग्रेशन बिना किसी अतिरिक्त शुल्क के शामिल है। अपने मौजूदा Excel या CSV रिकॉर्ड हमें भेजिए और हमारी टीम उन्हें आयात करके साफ़ और व्यवस्थित कर देती है — छात्र, स्टाफ़ और फीस संरचना, सब।",
      },
      "staff-roles": {
        title: "स्टाफ़ की भूमिकाएँ, अनुमतियाँ और विस्तृत पहुँच नियंत्रण",
        desc: "भूमिकाएँ आपके अपने स्टाफ़ पदनामों से बनती हैं, और हर भूमिका तय करती है कि वह कर्मचारी कौन से मॉड्यूल और रिकॉर्ड खोल सकता है। लॉगिन OTP से सत्यापित होते हैं, और स्कूल एडमिन, शिक्षकों, छात्रों तथा ग़ैर-शैक्षणिक स्टाफ़ — सबका अपना अलग लॉगिन होता है।",
      },
      "fee-structures": {
        title: "फीस हेड, संरचना और किस्तें सेट करना",
        desc: "फीस हेड और संरचना हर कक्षा के लिए तय होती है, और हर एक को अपनी देय तारीख़ों के साथ किस्तों में बाँटा जा सकता है। रियायतें हर छात्र पर अलग लागू होती हैं, और संरचना लाइव होते ही फीस रिमाइंडर ऐप नोटिफ़िकेशन तथा पोर्टल अलर्ट से अपने आप चलने लगते हैं।",
      },
      "receipts": {
        title: "रसीदें, बकाया खाता-बही और फीस निर्यात",
        desc: "हर भुगतान पर छात्र की फीस खाता-बही में दर्ज एक क्रमांकित डिजिटल रसीद बनती है, जिसे आप प्रिंट या साझा कर सकते हैं। फीस खाता-बही और बकाया रिपोर्ट जब चाहें Excel या PDF में निर्यात हो जाती हैं, प्रबंधन समीक्षा के लिए।",
      },
      "fee-article": {
        title: "फीस वसूली हमेशा 'लगभग लक्ष्य पर' क्यों रहती है, पूरी कभी नहीं होती",
        desc: "हर सत्र में फीस वसूली का आख़िरी 10-15% चुपचाप कहाँ रिस जाता है, और फ़ॉलो-अप में क्या बदलना चाहिए।",
      },
      "broadcasts": {
        title: "फीस रिमाइंडर, सर्कुलर और ब्रॉडकास्ट अलर्ट",
        desc: "देय तारीख़ निकलते ही फीस रिमाइंडर ऐप नोटिफ़िकेशन और पोर्टल अलर्ट के रूप में अपने आप चले जाते हैं। एडमिन और शिक्षक एक कक्षा या पूरे स्कूल को सर्कुलर और सूचनाएँ भी भेज सकते हैं, और ऐप के भीतर मौजूद चैट से अभिभावकों को सीधे संदेश कर सकते हैं।",
      },
      "whatsapp-article": {
        title: "आपके स्कूल का दफ़्तर WhatsApp ग्रुप से चल रहा है। इसकी असली क़ीमत क्या है।",
        desc: "जब फीस फ़ॉलो-अप, छुट्टी के आवेदन और सूचनाएँ सब बिना व्यवस्था के WhatsApp ग्रुप में रहते हैं, तो क्या-क्या बिगड़ता है।",
      },
      "marks-entry": {
        title: "परीक्षा योजना, अंक दर्ज करना और परिणाम तैयार करना",
        desc: "परीक्षाएँ निर्धारित कीजिए, शिक्षकों के लिए अंक दर्ज करना खोलिए, और कई सत्रों के परिणाम एक साथ जोड़िए। रिपोर्ट कार्ड इंजन फिर आपकी अपनी ग्रेडिंग व्यवस्था लागू करता है, जिसमें CBSE जैसे सख़्त पास नियम भी शामिल हैं जहाँ थ्योरी में फेल होने पर पूरा विषय फेल माना जाता है।",
      },
      "attendance": {
        title: "उपस्थिति: रोज़ाना कक्षा में, बायोमेट्रिक और जियोफ़ेंस के साथ",
        desc: "शिक्षक पोर्टल या ऐप से रोज़ाना कक्षा की उपस्थिति दर्ज करते हैं। स्टाफ़ उपस्थिति में इसके अलावा बायोमेट्रिक और फ़ेस एनरोलमेंट, जियोफ़ेंस चेक-इन और सुधार के लिए पंच समीक्षा स्क्रीन मिलती है। कक्षा, छात्र और स्टाफ़ की उपस्थिति रिपोर्ट जब चाहें निर्यात हो जाती हैं।",
      },
      "routes": {
        title: "रूट, स्टॉप, वाहन और ड्राइवर आवंटन",
        desc: "हर रूट को उसके स्टॉप के साथ मैप कीजिए, उसे वाहन और ड्राइवर सौंपिए, और छात्रों को उनके चढ़ने वाले स्टॉप से जोड़िए। परिवहन शुल्क बाक़ी फीस हेड की तरह ही छात्र की फीस संरचना से जुड़ जाता है।",
      },
      "audit-log": {
        title: "ऑडिट लॉग और गतिविधि इतिहास",
        desc: "संवेदनशील गतिविधियाँ एक ऐक्टिविटी लॉग में दर्ज रहती हैं जिसे आप कभी भी जाँच सकते हैं: फीस, अंक और छात्र रिकॉर्ड में किसने क्या बदला, और कब।",
      },
      "data-ownership": {
        title: "आपके स्कूल के डेटा का मालिक कौन है, और उसे निर्यात कैसे करें",
        desc: "आपके स्कूल के डेटा पर 100% मालिकाना हक़ आपका ही रहता है। छात्र रिकॉर्ड, फीस खाता-बही, उपस्थिति रजिस्टर और रिपोर्ट कार्ड कभी भी Excel या PDF में निर्यात हो जाते हैं, और बैकअप नियमित रूप से लिए जाते हैं।",
      },
      "status": {
        title: "प्लेटफ़ॉर्म की लाइव स्थिति और अपटाइम",
        desc: "Kaympus प्लेटफ़ॉर्म, मोबाइल ऐप, भुगतान और सूचना वितरण की मौजूदा उपलब्धता।",
      },
      "features": {
        title: "Kaympus की पूरी फ़ीचर सूची",
        desc: "हर मॉड्यूल एक ही सूची में: एडमिशन, उपस्थिति, समय-सारणी, फीस, परीक्षाएँ, पुस्तकालय, छात्रावास, परिवहन, HR और पेरोल।",
      },
      "plans": {
        title: "प्लान, कीमत और सब्सक्रिप्शन बिलिंग",
        desc: "हर प्लान में क्या शामिल है, कीमत कैसे तय होती है, और नवीनीकरण तथा इनवॉइस कैसे सँभाले जाते हैं।",
      },
      "buying-article": {
        title: "टियर 2 या टियर 3 शहर में स्कूल ERP ख़रीद रहे हैं? हस्ताक्षर से पहले ये 6 सवाल पूछिए।",
        desc: "किसी भी स्कूल ERP विक्रेता से प्रतिबद्ध होने से पहले पूछने लायक़ छह सवाल, और उनके जवाब आपको क्या बताते हैं।",
      },
      "contact": {
        title: "स्कूल सक्सेस टीम से बात कीजिए",
        desc: "वॉकथ्रू बुक कीजिए, या यहाँ दिए जवाबों में जो न मिला हो वह पूछिए — फ़ोन, ईमेल या तय समय पर कॉल के ज़रिए।",
      },
    },
  },

  blogStrip: {
    heading: "ब्लॉग से",
    body: "आधुनिक स्कूल चलाने के लिए गाइड, प्रोडक्ट अपडेट और विचार।",
    viewAll: "सभी पोस्ट देखें →",
  },

  footer: {
    blurb:
      "शैक्षणिक संस्थानों को एक संपूर्ण, 360-डिग्री प्रबंधन समाधान देते हुए। स्कूल के डिजिटल बदलाव में आपका भरोसेमंद साथी।",
    headingFeatures: "विशेषताएँ",
    headingCompany: "कंपनी",
    headingContact: "संपर्क",
    allFeatures: "सभी {count}+ विशेषताएँ",
    companyAbout: "हमारे बारे में",
    companyBlog: "ब्लॉग",
    companyPrivacy: "गोपनीयता नीति",
    companyTerms: "नियम और शर्तें",
    companyRefund: "धनवापसी नीति",
    bookDemo: "फ्री डेमो बुक करें",
    copyright: "© 2026 Kaympus • Kayease द्वारा। सर्वाधिकार सुरक्षित।",
    bottomSupport: "सपोर्ट",
    bottomPrivacy: "गोपनीयता",
    bottomTerms: "शर्तें",
    bottomRefund: "धनवापसी",
    floatingWhatsApp: "WhatsApp पर हमसे बात करें",
    floatingWhatsAppMsg: "नमस्ते! मैं Kaympus स्कूल मैनेजमेंट ERP के बारे में और जानना चाहता/चाहती हूँ।",
    floatingSupport: "डेमो या पूछताछ करें",
  },
};

export default hi;
